import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import DropdownArrow from '../components/DropdownArrow';
import { Container, Table, Button, Modal, Form, Alert, Pagination, Row, Col } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { farmWorkService, accountHolderService, equipmentHiringService } from '../services/apiClient';
import { notificationService } from '../services/notificationService';
import ConfirmationModal from '../components/ConfirmationModal';
import LoadingIndicator from '../components/LoadingIndicator';
import { useLocalization } from '../context/LocalizationContext';
import { getActiveSessionMonth, isSameMonth, setActiveSessionMonth, subscribeToActiveSessionMonth } from '../utils/sessionMonth';
import './FarmWork.css';
import {
  FarmWorkType,
  FarmWork,
  CreateFarmWorkTypeRequest,
  CreateFarmWorkRequest,
  AccountHolder,
  CreateFarmWorkContributionRequest,
  FarmWorkContribution,
  EquipmentHiring,
  CreateEquipmentHiringRequest,
  EquipmentHiringContribution,
  EquipmentHiringIncomeRecipient,
} from '../types/index';

export const FarmWorkPage: React.FC = () => {
  const { t, formatMonthYear, formatCurrency, formatDate, language } = useLocalization();
  const tRef = useRef(t);

  const getPreviousMonth = (): Date => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() - 1, 1);
  };

  const initialSelectedDateRef = useRef(getActiveSessionMonth(getPreviousMonth()));

  const toMonthKey = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const monthKeyToLabel = (monthKey: string): string => {
    const [year, month] = monthKey.split('-').map(Number);
    return formatMonthYear(new Date(year, month - 1, 1));
  };

  const parseNonNegativeAmount = (input: string): number => {
    if (!input.trim()) {
      return 0;
    }

    const normalizedInput = input.replace(',', '.');
    const parsedValue = Number.parseFloat(normalizedInput);

    if (Number.isNaN(parsedValue)) {
      return 0;
    }

    const clampedValue = Math.max(parsedValue, 0);
    return Math.round(clampedValue * 100) / 100;
  };

  const toIsoDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const tryParseEquipmentHiringDate = (value: string | undefined): Date | null => {
    if (!value || value.trim().length === 0) {
      return null;
    }

    const trimmed = value.trim();
    const localDateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
    if (localDateMatch) {
      const year = Number(localDateMatch[1]);
      const month = Number(localDateMatch[2]);
      const day = Number(localDateMatch[3]);
      const localDate = new Date(year, month - 1, day);
      if (!Number.isNaN(localDate.getTime())) {
        return localDate;
      }
    }

    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const parseEquipmentHiringDate = (value: string | undefined, fallbackDate: Date): Date => {
    return tryParseEquipmentHiringDate(value) ?? fallbackDate;
  };

  const isHolderTrackableForMonth = (holder: AccountHolder, monthDate: Date): boolean => {
    if (holder.isActive) {
      return true;
    }

    if (!holder.deactivatedDate) {
      return false;
    }

    const deactivated = new Date(holder.deactivatedDate);
    if (Number.isNaN(deactivated.getTime())) {
      return false;
    }

    const targetMonthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1).getTime();
    const deactivationMonthStart = new Date(deactivated.getFullYear(), deactivated.getMonth(), 1).getTime();
    return targetMonthStart < deactivationMonthStart;
  };

  // Farm Work Type state
  const [farmWorkTypes, setFarmWorkTypes] = useState<FarmWorkType[]>([]);
  const [typeCurrentPage, setTypeCurrentPage] = useState(1);
  const [typePageSize, setTypePageSize] = useState(10);
  const [showTypesManagementModal, setShowTypesManagementModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [editingTypeId, setEditingTypeId] = useState<number | null>(null);
  const [typeFormValidated, setTypeFormValidated] = useState(false);
  const [typeFormData, setTypeFormData] = useState<CreateFarmWorkTypeRequest>({
    name: '',
    description: '',
  });
  const [initialTypeFormData, setInitialTypeFormData] = useState<CreateFarmWorkTypeRequest>({
    name: '',
    description: '',
  });
  const [typeFormRows, setTypeFormRows] = useState<Array<{ name: string; description: string }>>([]);
  const [draftTypeName, setDraftTypeName] = useState('');
  const [draftTypeDescription, setDraftTypeDescription] = useState('');

  // Farm Work state
  const [farmWorks, setFarmWorks] = useState<FarmWork[]>([]);
  const [allAccountHolders, setAllAccountHolders] = useState<AccountHolder[]>([]);
  const [showWorkModal, setShowWorkModal] = useState(false);
  const [editingWorkId, setEditingWorkId] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(initialSelectedDateRef.current);
  const [debouncedSelectedDate, setDebouncedSelectedDate] = useState<Date>(initialSelectedDateRef.current);
  const [workModalDate, setWorkModalDate] = useState<Date>(initialSelectedDateRef.current);
  const [workCurrentPage, setWorkCurrentPage] = useState(1);
  const [workPageSize, setWorkPageSize] = useState(10);
  const [workFormData, setWorkFormData] = useState<CreateFarmWorkRequest>({
    farmWorkTypeId: 0,
    month: initialSelectedDateRef.current.getMonth() + 1,
    year: initialSelectedDateRef.current.getFullYear(),
    description: '',
    cost: 0,
  });
  const [selectedWorkTypeIds, setSelectedWorkTypeIds] = useState<number[]>([]);
  const [existingTypeIdsForModalMonth, setExistingTypeIdsForModalMonth] = useState<number[]>([]);
  const [selectedRecurringMonthKeys, setSelectedRecurringMonthKeys] = useState<string[]>([toMonthKey(initialSelectedDateRef.current)]);
  const [recurringMonthWarning, setRecurringMonthWarning] = useState<string | null>(null);
  const [workFormValidated, setWorkFormValidated] = useState(false);

  // Contribution state
  const [showContributionModal, setShowContributionModal] = useState(false);
  const [selectedFarmWork, setSelectedFarmWork] = useState<FarmWork | null>(null);
  const [selectedTypeIdForContribution, setSelectedTypeIdForContribution] = useState<number>(0);
  const [newWorkCostForContribution, setNewWorkCostForContribution] = useState<number>(0);
  const [selectedAccountHolderId, setSelectedAccountHolderId] = useState<number>(0);
  const [contributionAmount, setContributionAmount] = useState<number>(0);
  const [contributionFormValidated, setContributionFormValidated] = useState(false);
  const [showEditContributionModal, setShowEditContributionModal] = useState(false);
  const [editingContribution, setEditingContribution] = useState<FarmWorkContribution | null>(null);
  const [editingContributionAmount, setEditingContributionAmount] = useState<number>(0);
  const [editingContributionMaxAmount, setEditingContributionMaxAmount] = useState<number>(0);
  const [contributionCurrentPages, setContributionCurrentPages] = useState<Record<number, number>>({});
  const [contributionPageSize, setContributionPageSize] = useState(10);
  const [expandedContributorRows, setExpandedContributorRows] = useState<Record<number, boolean>>({});

  // Draft contributors for batch add (in Add Expense modal)
  const [draftContributorsToAdd, setDraftContributorsToAdd] = useState<Array<{ accountHolderId: number; amount: number }>>([]);
  const [draftContributorHolderId, setDraftContributorHolderId] = useState<number>(0);
  const [draftContributorAmount, setDraftContributorAmount] = useState<number>(0);
  const [workModalNewTypeName, setWorkModalNewTypeName] = useState('');
  const [workModalNewTypeDescription, setWorkModalNewTypeDescription] = useState('');
  const [isCreatingWorkModalType, setIsCreatingWorkModalType] = useState(false);

  // Equipment Hiring state
  const [activeSection, setActiveSection] = useState<'farmWork' | 'equipment'>('farmWork');
  const [equipmentHirings, setEquipmentHirings] = useState<EquipmentHiring[]>([]);
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);
  const [editingEquipmentId, setEditingEquipmentId] = useState<number | null>(null);
  const [isSavingEquipment, setIsSavingEquipment] = useState(false);
  const [equipmentCurrentPage, setEquipmentCurrentPage] = useState(1);
  const [equipmentPageSize, setEquipmentPageSize] = useState(10);
  const [equipmentFormData, setEquipmentFormData] = useState<CreateEquipmentHiringRequest>({
    equipmentDescription: '',
    cost: 0,
    hoursHired: 0,
    incomeAmount: 0,
    hiringDate: toIsoDateString(initialSelectedDateRef.current),
    month: initialSelectedDateRef.current.getMonth() + 1,
    year: initialSelectedDateRef.current.getFullYear(),
    description: '',
  });
  const [draftEquipmentContributors, setDraftEquipmentContributors] = useState<Array<{ accountHolderId: number; amount: number }>>([]);
  const [draftEquipmentContributorHolderId, setDraftEquipmentContributorHolderId] = useState<number>(0);
  const [draftEquipmentContributorAmount, setDraftEquipmentContributorAmount] = useState<number>(0);
  const [draftEquipmentIncomeRecipients, setDraftEquipmentIncomeRecipients] = useState<Array<{ accountHolderId: number; incomeShare: number }>>([]);
  const [draftIncomeRecipientHolderId, setDraftIncomeRecipientHolderId] = useState<number>(0);
  const [draftIncomeRecipientAmount, setDraftIncomeRecipientAmount] = useState<number>(0);
  const [expandedEquipmentRows, setExpandedEquipmentRows] = useState<Record<number, boolean>>({});
  const [showEquipmentContributionModal, setShowEquipmentContributionModal] = useState(false);
  const [selectedEquipmentForContribution, setSelectedEquipmentForContribution] = useState<EquipmentHiring | null>(null);
  const [editingEquipmentContribution, setEditingEquipmentContribution] = useState<EquipmentHiringContribution | null>(null);
  const [equipmentContributionHolderId, setEquipmentContributionHolderId] = useState<number>(0);
  const [equipmentContributionAmount, setEquipmentContributionAmount] = useState<number>(0);
  const [isSavingEquipmentContribution, setIsSavingEquipmentContribution] = useState(false);
  const [showEquipmentIncomeRecipientModal, setShowEquipmentIncomeRecipientModal] = useState(false);
  const [selectedEquipmentForIncomeRecipient, setSelectedEquipmentForIncomeRecipient] = useState<EquipmentHiring | null>(null);
  const [editingEquipmentIncomeRecipient, setEditingEquipmentIncomeRecipient] = useState<EquipmentHiringIncomeRecipient | null>(null);
  const [equipmentIncomeRecipientHolderId, setEquipmentIncomeRecipientHolderId] = useState<number>(0);
  const [equipmentIncomeRecipientAmount, setEquipmentIncomeRecipientAmount] = useState<number>(0);
  const [isSavingEquipmentIncomeRecipient, setIsSavingEquipmentIncomeRecipient] = useState(false);

  // Confirmation modal state
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationConfig, setConfirmationConfig] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
    variant?: 'danger' | 'warning' | 'info' | 'success';
    confirmText?: string;
  } | null>(null);

  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [isMonthRefreshing, setIsMonthRefreshing] = useState(false);
  const [isSavingType, setIsSavingType] = useState(false);
  const [isSavingWork, setIsSavingWork] = useState(false);
  const [isSavingContribution, setIsSavingContribution] = useState(false);
  const [isUpdatingContribution, setIsUpdatingContribution] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasInitializedRef = useRef(false);
  const monthRequestCacheRef = useRef<Map<string, Promise<FarmWork[]>>>(new Map());
  const latestMonthLoadRequestIdRef = useRef(0);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  useEffect(() => {
    return subscribeToActiveSessionMonth((nextMonth) => {
      setSelectedDate((previousDate) => (isSameMonth(previousDate, nextMonth) ? previousDate : nextMonth));
    });
  }, []);

  useEffect(() => {
    setActiveSessionMonth(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedSelectedDate((previousDate) => {
        if (
          previousDate.getFullYear() === selectedDate.getFullYear()
          && previousDate.getMonth() === selectedDate.getMonth()
        ) {
          return previousDate;
        }

        return selectedDate;
      });
    }, 150);

    return () => clearTimeout(debounceTimer);
  }, [selectedDate]);

  const loadData = useCallback(async (
    targetDate: Date,
    options?: { fullPage?: boolean; includeStaticData?: boolean; forceRefresh?: boolean }
  ) => {
    const fullPage = options?.fullPage ?? false;
    const includeStaticData = options?.includeStaticData ?? false;
    const forceRefresh = options?.forceRefresh ?? false;
    const requestId = ++latestMonthLoadRequestIdRef.current;

    try {
      if (fullPage) {
        setLoading(true);
      } else {
        setIsMonthRefreshing(true);
      }

      setError(null);

      if (includeStaticData) {
        const [types, holders] = await Promise.all([
          farmWorkService.getAllTypes(),
          accountHolderService.getAll(),
        ]);

        setFarmWorkTypes(types);
        setAllAccountHolders(holders);
      }

      const month = targetDate.getMonth() + 1;
      const year = targetDate.getFullYear();

      const requestKey = `${year}-${month}`;
      if (forceRefresh) {
        monthRequestCacheRef.current.delete(requestKey);
      }

      const existingRequest = forceRefresh ? undefined : monthRequestCacheRef.current.get(requestKey);
      const monthRequest = existingRequest || farmWorkService.getByMonthYear(month, year);

      if (!existingRequest) {
        monthRequestCacheRef.current.set(requestKey, monthRequest);
      }

      const [works, equipmentItems] = await Promise.all([
        monthRequest,
        equipmentHiringService.getByMonthYear(month, year),
      ]);
      if (requestId !== latestMonthLoadRequestIdRef.current) {
        return;
      }

      setFarmWorks(works);
      setEquipmentHirings(equipmentItems);
    } catch (err) {
      if (requestId !== latestMonthLoadRequestIdRef.current) {
        return;
      }

      const failureMessage = tRef.current('failedToLoadData');
      setError(failureMessage);
      notificationService.error(failureMessage);
      console.error(err);
    } finally {
      const month = targetDate.getMonth() + 1;
      const year = targetDate.getFullYear();
      const requestKey = `${year}-${month}`;
      monthRequestCacheRef.current.delete(requestKey);

      if (requestId !== latestMonthLoadRequestIdRef.current) {
        return;
      }

      if (fullPage) {
        setLoading(false);
        setHasLoadedOnce(true);
      } else {
        setIsMonthRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    loadData(initialSelectedDateRef.current, { fullPage: true, includeStaticData: true });
  }, [loadData]);

  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      return;
    }

    loadData(debouncedSelectedDate);
  }, [debouncedSelectedDate, loadData]);

  useEffect(() => {
    setWorkCurrentPage(1);
  }, [selectedDate, workPageSize, farmWorks.length]);

  useEffect(() => {
    setEquipmentCurrentPage(1);
  }, [selectedDate, equipmentPageSize, equipmentHirings.length]);

  useEffect(() => {
    setTypeCurrentPage(1);
  }, [typePageSize, farmWorkTypes.length]);

  useEffect(() => {
    setContributionCurrentPages({});
  }, [selectedDate, contributionPageSize, farmWorks.length]);

  useEffect(() => {
    setExpandedContributorRows((previous) => {
      const next: Record<number, boolean> = {};
      farmWorks.forEach((work) => {
        if (previous[work.id]) {
          next[work.id] = true;
        }
      });
      return next;
    });
  }, [farmWorks]);

  useEffect(() => {
    const loadExistingTypesForModalMonth = async () => {
      if (!showWorkModal || editingWorkId) {
        setExistingTypeIdsForModalMonth([]);
        return;
      }

      try {
        const month = workModalDate.getMonth() + 1;
        const year = workModalDate.getFullYear();
        const existingWorks = await farmWorkService.getByMonthYear(month, year);
        setExistingTypeIdsForModalMonth(existingWorks.map((work) => work.farmWorkTypeId));
      } catch (err) {
        setExistingTypeIdsForModalMonth([]);
        console.error(err);
      }
    };

    loadExistingTypesForModalMonth();
  }, [showWorkModal, editingWorkId, workModalDate]);

  const availableFarmWorkTypesForModal = useMemo(() => {
    if (editingWorkId) {
      return farmWorkTypes;
    }

    return farmWorkTypes.filter((type) => !existingTypeIdsForModalMonth.includes(type.id));
  }, [editingWorkId, farmWorkTypes, existingTypeIdsForModalMonth]);

  useEffect(() => {
    if (!showWorkModal || editingWorkId) {
      return;
    }

    if (availableFarmWorkTypesForModal.length === 0) {
      const monthKey = toMonthKey(workModalDate);
      setSelectedRecurringMonthKeys((prev) => prev.filter((key) => key !== monthKey));
      return;
    }

    const selectedTypeStillAvailable = availableFarmWorkTypesForModal.some(
      (type) => type.id === workFormData.farmWorkTypeId
    );

    if (!selectedTypeStillAvailable) {
      setWorkFormData((prev) => ({ ...prev, farmWorkTypeId: availableFarmWorkTypesForModal[0].id }));
    }
  }, [showWorkModal, editingWorkId, availableFarmWorkTypesForModal, workFormData.farmWorkTypeId, workModalDate]);

  const typeIdsWithContributors = useMemo(() => {
    const activeTypeIds = new Set(farmWorkTypes.filter((type) => type.isActive).map((type) => type.id));

    return new Set(
      farmWorks
        .filter((entry) => entry.isActive && activeTypeIds.has(entry.farmWorkTypeId))
        .filter((entry) =>
          Array.isArray(entry.contributions)
          && entry.contributions.some((contribution) => {
            const maybeContribution = contribution as FarmWorkContribution & { isActive?: boolean };
            return maybeContribution.isActive !== false && maybeContribution.amountContributed > 0;
          })
        )
        .map((entry) => entry.farmWorkTypeId)
    );
  }, [farmWorkTypes, farmWorks]);

  const filledTypeRowsCount = useMemo(
    () => typeFormRows.filter((row) => row.name.trim().length > 0).length,
    [typeFormRows]
  );

  const typeModalHasUnsavedChanges = useMemo(() => {
    if (editingTypeId) {
      return (
        typeFormData.name !== initialTypeFormData.name ||
        typeFormData.description !== initialTypeFormData.description
      );
    }

    return (
      typeFormRows.some((row) => row.name.trim().length > 0 || row.description.trim().length > 0)
      || draftTypeName.trim().length > 0
      || draftTypeDescription.trim().length > 0
    );
  }, [editingTypeId, typeFormData, initialTypeFormData, typeFormRows, draftTypeName, draftTypeDescription]);

  // Farm Work Type handlers
  const handleShowTypeModal = (type?: FarmWorkType) => {
    setTypeFormValidated(false);

    if (type) {
      setEditingTypeId(type.id);
      const nextFormData = {
        name: type.name,
        description: type.description,
      };
      setInitialTypeFormData(nextFormData);
      setTypeFormData(nextFormData);
    } else {
      setEditingTypeId(null);
      setInitialTypeFormData({ name: '', description: '' });
      setTypeFormData({ name: '', description: '' });
      setTypeFormRows([]);
      setDraftTypeName('');
      setDraftTypeDescription('');
    }
    setShowTypeModal(true);
  };

  const handleCloseTypeModal = () => {
      if (isSavingType) return;
    setShowTypeModal(false);
    setEditingTypeId(null);
    setTypeFormValidated(false);
    setInitialTypeFormData({ name: '', description: '' });
    setTypeFormData({ name: '', description: '' });
    setTypeFormRows([]);
    setDraftTypeName('');
    setDraftTypeDescription('');
  };

  const handleCancelTypeModal = () => {
    if (!typeModalHasUnsavedChanges) {
      handleCloseTypeModal();
      return;
    }

    setConfirmationConfig({
      title: t('unsavedChanges'),
      message: t('cancelAddFarmWorkTypeConfirmMessage'),
      variant: 'success',
      confirmText: t('yes'),
      onConfirm: () => {
        setShowConfirmation(false);
        setConfirmationConfig(null);
        handleCloseTypeModal();
      },
    });
    setShowConfirmation(true);
  };

  const handleAddDraftType = () => {
    const trimmedName = draftTypeName.trim();
    const trimmedDescription = draftTypeDescription.trim();

    if (!trimmedName) {
      notificationService.error(t('expenseTypeNameRequired'));
      return;
    }

    if (typeFormRows.some((row) => row.name.trim().toLowerCase() === trimmedName.toLowerCase())) {
      notificationService.error(t('expenseTypeAlreadyAdded'));
      return;
    }

    setTypeFormRows((prev) => [...prev, { name: trimmedName, description: trimmedDescription }]);
    setDraftTypeName('');
    setDraftTypeDescription('');
    notificationService.success(t('expenseTypeAddedToList'));
  };

  const handleRemoveDraftType = (idx: number) => {
    setTypeFormRows(prev => prev.filter((_, i) => i !== idx));
  };

  const handleClearDraftTypes = () => {
    setTypeFormRows([]);
    setDraftTypeName('');
    setDraftTypeDescription('');
  };

  const handleSaveFarmWorkType = async () => {
    setTypeFormValidated(true);

    if (editingTypeId) {
      const trimmedName = typeFormData.name.trim();
      const trimmedDescription = typeFormData.description.trim();

      if (!trimmedName) {
        notificationService.error(t('expenseTypeNameRequired'));
        return;
      }

      try {
          setIsSavingType(true);
        await farmWorkService.updateType(editingTypeId, {
          name: trimmedName,
          description: trimmedDescription,
          isActive: true,
        });
        notificationService.success(t('farmWorkTypeUpdatedSuccessfully'));
        handleCloseTypeModal();
        await loadData(selectedDate, { includeStaticData: true, forceRefresh: true });
      } catch (err) {
        notificationService.error(t('failedToSaveFarmWorkType'));
        console.error(err);
        } finally {
          setIsSavingType(false);
      }
    } else {
      const validRows = typeFormRows.filter(r => r.name.trim());
      if (validRows.length === 0) {
        notificationService.error(t('expenseTypeNameRequired'));
        return;
      }

      try {
          setIsSavingType(true);
        for (const row of validRows) {
          await farmWorkService.createType({
            name: row.name.trim(),
            description: row.description.trim(),
          });
        }
        notificationService.success(
          validRows.length === 1 ? t('farmWorkTypeCreatedSuccessfully') : t('farmWorkTypesCreatedSuccessfully')
        );
        handleCloseTypeModal();
        await loadData(selectedDate, { includeStaticData: true, forceRefresh: true });
      } catch (err) {
        notificationService.error(t('failedToSaveFarmWorkType'));
        console.error(err);
        } finally {
          setIsSavingType(false);
      }
    }
  };

  const handleDeleteType = async (id: number) => {
    if (typeIdsWithContributors.has(id)) {
      notificationService.info(t('cannotDeleteExpenseTypeWithContributors'));
      return;
    }

    setConfirmationConfig({
      title: t('deleteFarmWorkTypeTitle'),
      message: t('deleteFarmWorkTypeConfirmMessage'),
      variant: 'danger',
      onConfirm: async () => {
        try {
          await farmWorkService.deleteType(id);
          notificationService.success(t('farmWorkTypeDeletedSuccessfully'));
          setShowConfirmation(false);
          await loadData(selectedDate, { includeStaticData: true, forceRefresh: true });
        } catch (err) {
          notificationService.error(t('failedToDeleteFarmWorkType'));
          console.error(err);
        }
      },
    });
    setShowConfirmation(true);
  };

  // Farm Work handlers
  const handleShowWorkModal = (work?: FarmWork) => {
    setRecurringMonthWarning(null);
    setWorkFormValidated(false);
    setWorkModalNewTypeName('');
    setWorkModalNewTypeDescription('');

    if (work) {
      setEditingWorkId(work.id);
      setWorkFormData({
        farmWorkTypeId: work.farmWorkTypeId,
        month: work.month,
        year: work.year,
        description: work.description,
        cost: work.cost,
      });
      // Set date picker to the work item's month/year
      const date = new Date(work.year, work.month - 1, 1);
      setWorkModalDate(date);
      setSelectedRecurringMonthKeys([toMonthKey(date)]);
    } else {
      setEditingWorkId(null);
      const createDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
      setWorkModalDate(createDate);
      setWorkFormData({
        farmWorkTypeId: farmWorkTypes[0]?.id || 0,
        month: createDate.getMonth() + 1,
        year: createDate.getFullYear(),
        description: '',
        cost: 0,
      });
      setSelectedRecurringMonthKeys([toMonthKey(createDate)]);
    }
    setShowWorkModal(true);
  };

  const handleCloseWorkModal = () => {
      if (isSavingWork) return;
    setShowWorkModal(false);
    setEditingWorkId(null);
    setSelectedRecurringMonthKeys([toMonthKey(initialSelectedDateRef.current)]);
    setRecurringMonthWarning(null);
    setWorkFormValidated(false);
    setWorkFormData({
      farmWorkTypeId: 0,
      month: initialSelectedDateRef.current.getMonth() + 1,
      year: initialSelectedDateRef.current.getFullYear(),
      description: '',
      cost: 0,
    });
    setSelectedWorkTypeIds([]);
    setWorkModalNewTypeName('');
    setWorkModalNewTypeDescription('');
    handleClearDraftContributors();
  };

  const handleCancelWorkModal = () => {
    if (editingWorkId) {
      handleCloseWorkModal();
      return;
    }

    setConfirmationConfig({
      title: t('unsavedChanges'),
      message: t('cancelAddFarmWorkConfirmMessage'),
      variant: 'success',
      confirmText: t('yes'),
      onConfirm: () => {
        setShowConfirmation(false);
        setConfirmationConfig(null);
        handleCloseWorkModal();
      },
    });
    setShowConfirmation(true);
  };

  const addRecurringMonths = async (monthKeys: string[]) => {
    setRecurringMonthWarning(null);
    const uniqueMonthKeys = Array.from(new Set(monthKeys));

    if (uniqueMonthKeys.length === 0) {
      return;
    }

    const availableMonthKeys: string[] = [];
    const skippedMonthKeys: string[] = [];

    try {
      for (const monthKey of uniqueMonthKeys) {
        const [year, month] = monthKey.split('-').map(Number);
        const existingEntries = await farmWorkService.getByMonthYear(month, year);
        const existingTypeIds = new Set(existingEntries.map((entry) => entry.farmWorkTypeId));
        const hasAvailableTypes = farmWorkTypes.some((type) => !existingTypeIds.has(type.id));

        if (!hasAvailableTypes) {
          skippedMonthKeys.push(monthKey);
          continue;
        }

        availableMonthKeys.push(monthKey);
      }
    } catch (err) {
      notificationService.error(t('failedValidateAvailableTypesMonth'));
      console.error(err);
      return;
    }

    if (availableMonthKeys.length > 0) {
      setSelectedRecurringMonthKeys((prev) => Array.from(new Set([...prev, ...availableMonthKeys])).sort());
    }

    if (skippedMonthKeys.length > 0) {
      const skippedLabels = skippedMonthKeys.map(monthKeyToLabel).join(', ');
      const warningMessage = `${t('skippedMonthsPrefix')} ${skippedMonthKeys.length} ${skippedMonthKeys.length > 1 ? t('monthsPluralLabel') : t('monthSingularLabel')} ${t('skippedMonthsAllTypesExistSuffix')}: ${skippedLabels}`;
      setRecurringMonthWarning(warningMessage);
      notificationService.info(warningMessage);
    }
  };

  const handleAddRecurringMonth = async () => {
    const monthKey = toMonthKey(workModalDate);
    await addRecurringMonths([monthKey]);
  };

  const handleAddRecurringPreset = async (monthCount: number) => {
    const monthKeys = Array.from({ length: monthCount }, (_, index) => {
      const monthDate = new Date(workModalDate.getFullYear(), workModalDate.getMonth() + index, 1);
      return toMonthKey(monthDate);
    });

    await addRecurringMonths(monthKeys);
  };

  const handleClearRecurringMonths = () => {
    setRecurringMonthWarning(null);
    setSelectedRecurringMonthKeys([]);
  };

  const handleRemoveRecurringMonth = (monthKey: string) => {
    setRecurringMonthWarning(null);
    setSelectedRecurringMonthKeys((prev) => prev.filter((key) => key !== monthKey));
  };

  const handleCreateTypeFromWorkModal = async () => {
    const trimmedName = workModalNewTypeName.trim();
    const trimmedDescription = workModalNewTypeDescription.trim();

    if (!trimmedName) {
      notificationService.error(t('expenseTypeNameRequired'));
      return;
    }

    if (farmWorkTypes.some((type) => type.name.trim().toLowerCase() === trimmedName.toLowerCase())) {
      notificationService.error(t('expenseTypeAlreadyAdded'));
      return;
    }

    try {
      setIsCreatingWorkModalType(true);
      const createdType = await farmWorkService.createType({
        name: trimmedName,
        description: trimmedDescription,
      });
      const refreshedTypes = await farmWorkService.getAllTypes();
      setFarmWorkTypes(refreshedTypes);

      const fallbackTypeId = refreshedTypes.find(
        (type) => type.name.trim().toLowerCase() === trimmedName.toLowerCase()
      )?.id;
      const createdTypeId = typeof createdType?.id === 'number' ? createdType.id : fallbackTypeId;

      if (typeof createdTypeId === 'number') {
        setSelectedWorkTypeIds((previous) => (
          previous.includes(createdTypeId) ? previous : [...previous, createdTypeId]
        ));
      }

      setWorkModalNewTypeName('');
      setWorkModalNewTypeDescription('');
      notificationService.success(t('farmWorkTypeCreatedSuccessfully'));
    } catch (err) {
      notificationService.error(t('failedToSaveFarmWorkType'));
      console.error(err);
    } finally {
      setIsCreatingWorkModalType(false);
    }
  };

  const handleSaveFarmWork = async () => {
    setWorkFormValidated(true);

    try {
        setIsSavingWork(true);
      // Validate that farm work type(s) are selected
      if (!editingWorkId && selectedWorkTypeIds.length === 0) {
        notificationService.error(t('pleaseSelectFarmWorkType'));
        return;
      }

      if (workFormData.cost <= 0) {
        notificationService.error(t('expenseCostMustBeGreaterThanZero'));
        return;
      }

      if (editingWorkId) {
        await farmWorkService.update(editingWorkId, {
          ...workFormData,
          isActive: true,
        });
        notificationService.success(t('farmWorkUpdatedSuccessfully'));
      } else {
        const monthKeysToProcess = selectedRecurringMonthKeys.length > 0
          ? [...selectedRecurringMonthKeys].sort()
          : [toMonthKey(workModalDate)];

        const eligibleMonthKeys: string[] = [];
        const removedMonthKeys: string[] = [];
        const skippedExistingTypeMonthKeys: string[] = [];
        const createdWorks: FarmWork[] = [];
        let createdCount = 0;

        for (const monthKey of monthKeysToProcess) {
          const [year, month] = monthKey.split('-').map(Number);
          const existingEntries = await farmWorkService.getByMonthYear(month, year);
          const existingTypeIds = new Set(existingEntries.map((entry) => entry.farmWorkTypeId));
          const hasAvailableTypes = farmWorkTypes.some((type) => !existingTypeIds.has(type.id));

          if (!hasAvailableTypes) {
            removedMonthKeys.push(monthKey);
            continue;
          }

          eligibleMonthKeys.push(monthKey);

          // Create an expense for each selected type
          for (const typeId of selectedWorkTypeIds) {
            const matchingEntry = existingEntries.find(
              (entry) => entry.farmWorkTypeId === typeId
            );

            if (matchingEntry) {
              skippedExistingTypeMonthKeys.push(monthKey);
            } else {
              const createdWork = await farmWorkService.create({
                ...workFormData,
                farmWorkTypeId: typeId,
                month,
                year,
              });
              createdWorks.push(createdWork);
              createdCount += 1;
            }
          }
        }

        if (removedMonthKeys.length > 0) {
          setSelectedRecurringMonthKeys((prev) => prev.filter((key) => !removedMonthKeys.includes(key)));
          notificationService.info(
            `${t('removedMonthsPrefix')} ${removedMonthKeys.length} ${removedMonthKeys.length > 1 ? t('monthsPluralLabel') : t('monthSingularLabel')} ${t('removedMonthsNoAvailableTypesSuffix')}`
          );
        }

        if (skippedExistingTypeMonthKeys.length > 0) {
          const skippedLabels = skippedExistingTypeMonthKeys.map(monthKeyToLabel).join(', ');
          setRecurringMonthWarning(
            `${t('skippedMonthsPrefix')} ${skippedExistingTypeMonthKeys.length} ${skippedExistingTypeMonthKeys.length > 1 ? t('monthsPluralLabel') : t('monthSingularLabel')} ${t('skippedMonthsTypeExistsSuffix')}: ${skippedLabels}`
          );
          notificationService.warning(
            `${t('skippedMonthsPrefix')} ${skippedExistingTypeMonthKeys.length} ${skippedExistingTypeMonthKeys.length > 1 ? t('monthsPluralLabel') : t('monthSingularLabel')} ${t('skippedMonthsTypeExistsSuffix')}: ${skippedLabels}`
          );
        } else {
          setRecurringMonthWarning(null);
        }

        if (eligibleMonthKeys.length === 0) {
          notificationService.error(t('noSelectedMonthsHaveAvailableTypes'));
          return;
        }

        if (createdCount === 0 && skippedExistingTypeMonthKeys.length > 0) {
          return;
        }

        notificationService.success(
          `${t('processedMonthsPrefix')} ${eligibleMonthKeys.length} ${eligibleMonthKeys.length > 1 ? t('monthsPluralLabel') : t('monthSingularLabel')}: ${createdCount} ${t('processedMonthsCreatedSuffix')}`
        );

        // Add draft contributors to all created farm works
        if (createdWorks.length > 0 && draftContributorsToAdd.length > 0) {
          await addDraftContributorsToFarmWork(createdWorks.map(w => w.id));
          // Reload to show new contributions
          handleCloseWorkModal();
          await loadData(selectedDate, { forceRefresh: true });
          return;
        }

      }
      handleCloseWorkModal();
      await loadData(selectedDate, { forceRefresh: true });
    } catch (err) {
      notificationService.error(t('failedToSaveFarmWork'));
      console.error(err);
      } finally {
        setIsSavingWork(false);
    }
  };

  const handleDeleteWork = async (id: number) => {
    setConfirmationConfig({
      title: t('deleteFarmWorkTitle'),
      message: t('deleteFarmWorkConfirmMessage'),
      variant: 'danger',
      onConfirm: async () => {
        try {
          await farmWorkService.delete(id);
          notificationService.success(t('farmWorkDeletedSuccessfully'));
          setShowConfirmation(false);
          await loadData(selectedDate, { forceRefresh: true });
        } catch (err) {
          notificationService.error(t('failedToDeleteFarmWork'));
          console.error(err);
        }
      },
    });
    setShowConfirmation(true);
  };

  // Contribution handlers
  const handleShowContributionModal = (work: FarmWork) => {
    const contributions = work.contributions || [];
    const totalContributions = contributions.reduce((sum, contribution) => sum + contribution.amountContributed, 0);
    const outstandingCost = work.cost - totalContributions;

    if (outstandingCost <= 0) {
      notificationService.info(t('farmWorkAlreadyFullyCovered'));
      return;
    }

    setSelectedFarmWork(work);
    setSelectedAccountHolderId(activeAccountHolders[0]?.id || 0);
    setContributionAmount(0);
    setContributionFormValidated(false);
    setShowContributionModal(true);
  };

  const handleCloseContributionModal = () => {
    if (isSavingContribution) return;
    setShowContributionModal(false);
    setSelectedFarmWork(null);
    setSelectedTypeIdForContribution(0);
    setNewWorkCostForContribution(0);
    setSelectedAccountHolderId(0);
    setContributionAmount(0);
    setContributionFormValidated(false);
  };

  const handleAddContribution = async () => {
    setContributionFormValidated(true);

    // Global mode: need to create a farm work entry from the selected expense type
    if (selectedFarmWork === null) {
      if (selectedTypeIdForContribution <= 0) return;
      if (newWorkCostForContribution <= 0) {
        notificationService.error(t('expenseCostMustBeGreaterThanZero'));
        return;
      }
      if (selectedAccountHolderId <= 0) {
        notificationService.error(t('pleaseSelectAccountHolder'));
        return;
      }
      if (contributionAmount <= 0) {
        notificationService.error(t('contributionAmountMustBeGreaterThanZero'));
        return;
      }
      if (contributionAmount > newWorkCostForContribution) {
        notificationService.error(`${t('contributionCannotExceedOutstandingCost')} ${formatCurrency(newWorkCostForContribution)}`);
        return;
      }
      try {
        setIsSavingContribution(true);
        const newWork = await farmWorkService.create({
          farmWorkTypeId: selectedTypeIdForContribution,
          month: selectedDate.getMonth() + 1,
          year: selectedDate.getFullYear(),
          description: '',
          cost: newWorkCostForContribution,
        });
        await farmWorkService.addContribution({
          farmWorkId: newWork.id,
          accountHolderId: selectedAccountHolderId,
          amountContributed: contributionAmount,
        });
        notificationService.success(t('contributionAddedSuccessfully'));
        handleCloseContributionModal();
        await loadData(selectedDate, { forceRefresh: true });
      } catch (err: any) {
        const errorMessage = err?.response?.data;
        notificationService.error(
          typeof errorMessage === 'string' && errorMessage.trim().length > 0
            ? errorMessage
              : t('failedToAddContribution')
        );
        console.error(err);
      } finally {
        setIsSavingContribution(false);
      }
      return;
    }

    // Per-row mode: add to existing farm work entry
    const totalContributions = selectedFarmWork.contributions.reduce(
      (sum, contribution) => sum + contribution.amountContributed,
      0
    );
    const outstandingCost = selectedFarmWork.cost - totalContributions;

    if (outstandingCost <= 0) {
      notificationService.info(t('farmWorkAlreadyFullyCovered'));
      return;
    }

    if (selectedAccountHolderId <= 0) {
      notificationService.error(t('pleaseSelectAccountHolder'));
      return;
    }

    if (contributionAmount <= 0) {
      notificationService.error(t('contributionAmountMustBeGreaterThanZero'));
      return;
    }

    if (contributionAmount > outstandingCost) {
      notificationService.error(`${t('contributionCannotExceedOutstandingCost')} ${formatCurrency(outstandingCost)}`);
      return;
    }

    try {
      setIsSavingContribution(true);
      const data: CreateFarmWorkContributionRequest = {
        farmWorkId: selectedFarmWork.id,
        accountHolderId: selectedAccountHolderId,
        amountContributed: contributionAmount,
      };
      await farmWorkService.addContribution(data);
      notificationService.success(t('contributionAddedSuccessfully'));
      handleCloseContributionModal();
      await loadData(selectedDate, { forceRefresh: true });
    } catch (err: any) {
      const errorMessage = err?.response?.data;
      notificationService.error(
        typeof errorMessage === 'string' && errorMessage.trim().length > 0
          ? errorMessage
            : t('failedToAddContribution')
      );
      console.error(err);
    } finally {
      setIsSavingContribution(false);
    }
  };

  const handleRemoveContribution = async (contributionId: number) => {
    setConfirmationConfig({
      title: t('removeContributionTitle'),
      message: t('removeContributionConfirmMessage'),
      variant: 'danger',
      onConfirm: async () => {
        try {
          await farmWorkService.removeContribution(contributionId);
          notificationService.success(t('contributionRemovedSuccessfully'));
          setShowConfirmation(false);
          await loadData(selectedDate, { forceRefresh: true });
        } catch (err) {
          notificationService.error(t('failedToRemoveContribution'));
          console.error(err);
        }
      },
    });
    setShowConfirmation(true);
  };

  const handleShowEditContributionModal = (work: FarmWork, contribution: FarmWorkContribution) => {
    const otherContributionsTotal = work.contributions
      .filter((item) => item.id !== contribution.id)
      .reduce((sum, item) => sum + item.amountContributed, 0);

    const maxAllowed = Math.max(work.cost - otherContributionsTotal, 0);

    setSelectedFarmWork(work);
    setEditingContribution(contribution);
    setEditingContributionAmount(contribution.amountContributed);
    setEditingContributionMaxAmount(maxAllowed);
    setShowEditContributionModal(true);
  };

  const handleToggleContributorRow = (workId: number) => {
    setExpandedContributorRows((previous) => ({
      ...previous,
      [workId]: !previous[workId],
    }));
  };

  const handleCloseEditContributionModal = () => {
    if (isUpdatingContribution) return;
    setShowEditContributionModal(false);
    setEditingContribution(null);
    setEditingContributionAmount(0);
    setEditingContributionMaxAmount(0);
  };

  const handleUpdateContribution = async () => {
    if (!editingContribution) {
      return;
    }

    if (editingContributionAmount <= 0) {
      notificationService.error(t('contributionAmountMustBeGreaterThanZero'));
      return;
    }

    if (editingContributionAmount > editingContributionMaxAmount) {
      notificationService.error(
        `${t('contributionCannotExceedAllowableAmount')} ${formatCurrency(editingContributionMaxAmount)}`
      );
      return;
    }

    try {
      setIsUpdatingContribution(true);
      await farmWorkService.updateContribution(editingContribution.id, {
        amountContributed: editingContributionAmount,
      });

      notificationService.success(t('contributionUpdatedSuccessfully'));
      handleCloseEditContributionModal();
      await loadData(selectedDate, { forceRefresh: true });
    } catch (err: any) {
      const errorMessage = err?.response?.data;
      notificationService.error(
        typeof errorMessage === 'string' && errorMessage.trim().length > 0
          ? errorMessage
            : t('failedToUpdateContribution')
      );
      console.error(err);
    } finally {
      setIsUpdatingContribution(false);
    }
  };

  // Draft contributor handlers (for batch adding in Add Expense modal)
  const handleAddDraftContributor = () => {
    if (draftContributorHolderId === 0) {
      notificationService.error(t('pleaseSelectAccountHolderForContribution'));
      return;
    }

    if (draftContributorAmount <= 0) {
      notificationService.error(t('contributionAmountMustBeGreaterThanZero'));
      return;
    }

    // Check if holder is already in list
    if (draftContributorsToAdd.some(c => c.accountHolderId === draftContributorHolderId)) {
      notificationService.error(t('accountHolderAlreadyAdded'));
      return;
    }

    setDraftContributorsToAdd([...draftContributorsToAdd, {
      accountHolderId: draftContributorHolderId,
      amount: draftContributorAmount,
    }]);

    // Reset form
    setDraftContributorHolderId(0);
    setDraftContributorAmount(0);
    notificationService.success(t('contributorAddedToList'));
  };

  const handleRemoveDraftContributor = (accountHolderId: number) => {
    setDraftContributorsToAdd(draftContributorsToAdd.filter(c => c.accountHolderId !== accountHolderId));
  };

  const handleClearDraftContributors = () => {
    setDraftContributorsToAdd([]);
    setDraftContributorHolderId(0);
    setDraftContributorAmount(0);
  };

  const addDraftContributorsToFarmWork = async (farmWorkIds: number[]) => {
    if (draftContributorsToAdd.length === 0) {
      return;
    }

    try {
      let successCount = 0;
      for (const farmWorkId of farmWorkIds) {
        for (const contributor of draftContributorsToAdd) {
          const data: CreateFarmWorkContributionRequest = {
            farmWorkId,
            accountHolderId: contributor.accountHolderId,
            amountContributed: contributor.amount,
          };
          await farmWorkService.addContribution(data);
          successCount += 1;
        }
      }

      if (successCount > 0) {
        notificationService.success(
          `${successCount} ${successCount === 1 ? t('contributor') : t('contributorPlural')} ${t('addedSuccessfully')}`
        );
      }

      handleClearDraftContributors();
    } catch (err: any) {
      const errorMessage = err?.response?.data;
      notificationService.error(
        typeof errorMessage === 'string' && errorMessage.trim().length > 0
          ? errorMessage
          : t('failedToAddContributors')
      );
      console.error(err);
    }
  };

  const handleShowEquipmentModal = (equipment?: EquipmentHiring) => {
    if (equipment) {
      setEditingEquipmentId(equipment.id);
      const fallbackDate = new Date(equipment.year, equipment.month - 1, 1);
      const hiringDate = parseEquipmentHiringDate(equipment.hiringDate, fallbackDate);

      setEquipmentFormData({
        equipmentDescription: equipment.equipmentDescription,
        cost: equipment.cost,
        hoursHired: equipment.hoursHired,
        incomeAmount: equipment.incomeAmount,
        hiringDate: toIsoDateString(hiringDate),
        month: hiringDate.getMonth() + 1,
        year: hiringDate.getFullYear(),
        description: equipment.description,
      });
    } else {
      setEditingEquipmentId(null);
      setEquipmentFormData({
        equipmentDescription: '',
        cost: 0,
        hoursHired: 0,
        incomeAmount: 0,
        hiringDate: toIsoDateString(selectedDate),
        month: selectedDate.getMonth() + 1,
        year: selectedDate.getFullYear(),
        description: '',
      });
      setDraftEquipmentContributors([]);
      setDraftEquipmentContributorHolderId(0);
      setDraftEquipmentContributorAmount(0);
      setDraftEquipmentIncomeRecipients([]);
      setDraftIncomeRecipientHolderId(0);
      setDraftIncomeRecipientAmount(0);
    }

    setShowEquipmentModal(true);
  };

  const handleCloseEquipmentModal = () => {
    if (isSavingEquipment) return;
    setShowEquipmentModal(false);
    setEditingEquipmentId(null);
  };

  const handleCancelEquipmentModal = () => {
    if (editingEquipmentId) {
      handleCloseEquipmentModal();
      return;
    }

    setConfirmationConfig({
      title: t('unsavedChanges'),
      message: t('cancelAddEquipmentHiringConfirmMessage'),
      variant: 'success',
      confirmText: t('yes'),
      onConfirm: () => {
        setShowConfirmation(false);
        setConfirmationConfig(null);
        handleCloseEquipmentModal();
      },
    });
    setShowConfirmation(true);
  };

  const handleAddDraftEquipmentContributor = () => {
    if (draftEquipmentContributorHolderId <= 0) {
      notificationService.error(t('pleaseSelectAccountHolder'));
      return;
    }

    if (draftEquipmentContributorAmount <= 0) {
      notificationService.error(t('contributionAmountMustBeGreaterThanZero'));
      return;
    }

    if (draftEquipmentContributors.some((entry) => entry.accountHolderId === draftEquipmentContributorHolderId)) {
      notificationService.error(t('accountHolderAlreadyAdded'));
      return;
    }

    setDraftEquipmentContributors((previous) => [
      ...previous,
      { accountHolderId: draftEquipmentContributorHolderId, amount: draftEquipmentContributorAmount },
    ]);
    setDraftEquipmentContributorHolderId(0);
    setDraftEquipmentContributorAmount(0);
  };

  const handleRemoveDraftEquipmentContributor = (accountHolderId: number) => {
    setDraftEquipmentContributors((previous) => previous.filter((entry) => entry.accountHolderId !== accountHolderId));
  };

  const handleAddDraftIncomeRecipient = () => {
    if (draftIncomeRecipientHolderId <= 0) {
      notificationService.error(t('pleaseSelectAccountHolder'));
      return;
    }

    if (draftIncomeRecipientAmount <= 0) {
      notificationService.error(t('contributionAmountMustBeGreaterThanZero'));
      return;
    }

    if (draftEquipmentIncomeRecipients.some((entry) => entry.accountHolderId === draftIncomeRecipientHolderId)) {
      notificationService.error(t('accountHolderAlreadyAdded'));
      return;
    }

    setDraftEquipmentIncomeRecipients((previous) => [
      ...previous,
      { accountHolderId: draftIncomeRecipientHolderId, incomeShare: draftIncomeRecipientAmount },
    ]);
    setDraftIncomeRecipientHolderId(0);
    setDraftIncomeRecipientAmount(0);
  };

  const handleRemoveDraftIncomeRecipient = (accountHolderId: number) => {
    setDraftEquipmentIncomeRecipients((previous) => previous.filter((entry) => entry.accountHolderId !== accountHolderId));
  };

  const handleSaveEquipmentHiring = async () => {
    if (!equipmentFormData.equipmentDescription.trim()) {
      notificationService.error(t('equipmentRequired'));
      return;
    }

    if (!equipmentFormData.hiringDate) {
      notificationService.error(t('dateRequired'));
      return;
    }

    if (equipmentFormData.cost <= 0) {
      notificationService.error(t('expenseCostMustBeGreaterThanZero'));
      return;
    }

    try {
      setIsSavingEquipment(true);
      const parsedHiringDate = tryParseEquipmentHiringDate(equipmentFormData.hiringDate);
      if (!parsedHiringDate) {
        notificationService.error(t('pleaseSelectValidDate'));
        return;
      }

      const hiringMonth = parsedHiringDate.getMonth() + 1;
      const hiringYear = parsedHiringDate.getFullYear();
      const draftIncomeAmount = draftEquipmentIncomeRecipients.reduce((sum, recipient) => sum + recipient.incomeShare, 0);

      let equipmentId = editingEquipmentId;
      if (editingEquipmentId) {
        await equipmentHiringService.update(editingEquipmentId, {
          ...equipmentFormData,
          month: hiringMonth,
          year: hiringYear,
          isActive: true,
        });
      } else {
        const created = await equipmentHiringService.create({
          ...equipmentFormData,
          incomeAmount: draftIncomeAmount,
          month: hiringMonth,
          year: hiringYear,
        });
        equipmentId = created.id;
      }

      if (!editingEquipmentId && equipmentId) {
        for (const contributor of draftEquipmentContributors) {
          await equipmentHiringService.addContribution({
            equipmentHiringId: equipmentId,
            accountHolderId: contributor.accountHolderId,
            amountContributed: contributor.amount,
          });
        }

        for (const recipient of draftEquipmentIncomeRecipients) {
          await equipmentHiringService.addIncomeRecipient({
            equipmentHiringId: equipmentId,
            accountHolderId: recipient.accountHolderId,
            incomeShare: recipient.incomeShare,
          });
        }
      }

      notificationService.success(editingEquipmentId ? t('equipmentHiringUpdatedSuccessfully') : t('equipmentHiringCreatedSuccessfully'));
      handleCloseEquipmentModal();
      await loadData(selectedDate, { forceRefresh: true });
    } catch (err) {
      notificationService.error(t('failedToSaveEquipmentHiring'));
      console.error(err);
    } finally {
      setIsSavingEquipment(false);
    }
  };

  const handleDeleteEquipmentHiring = async (id: number) => {
    setConfirmationConfig({
      title: t('deleteEquipmentHiringTitle'),
      message: t('deleteEquipmentHiringConfirmMessage'),
      variant: 'danger',
      onConfirm: async () => {
        try {
          await equipmentHiringService.delete(id);
          setShowConfirmation(false);
          notificationService.success(t('equipmentHiringDeletedSuccessfully'));
          await loadData(selectedDate, { forceRefresh: true });
        } catch (err) {
          notificationService.error(t('failedToDeleteEquipmentHiring'));
          console.error(err);
        }
      },
    });
    setShowConfirmation(true);
  };

  const handleToggleEquipmentRow = (equipmentId: number) => {
    setExpandedEquipmentRows((previous) => ({
      ...previous,
      [equipmentId]: !previous[equipmentId],
    }));
  };

  const handleShowEquipmentContributionModal = (equipment: EquipmentHiring, contribution?: EquipmentHiringContribution) => {
    setSelectedEquipmentForContribution(equipment);

    if (contribution) {
      setEditingEquipmentContribution(contribution);
      setEquipmentContributionHolderId(contribution.accountHolderId);
      setEquipmentContributionAmount(contribution.amountContributed);
    } else {
      setEditingEquipmentContribution(null);
      setEquipmentContributionHolderId(0);
      setEquipmentContributionAmount(0);
    }

    setShowEquipmentContributionModal(true);
  };

  const handleCloseEquipmentContributionModal = () => {
    if (isSavingEquipmentContribution) return;

    setShowEquipmentContributionModal(false);
    setSelectedEquipmentForContribution(null);
    setEditingEquipmentContribution(null);
    setEquipmentContributionHolderId(0);
    setEquipmentContributionAmount(0);
  };

  const handleSaveEquipmentContribution = async () => {
    if (!selectedEquipmentForContribution) {
      return;
    }

    if (!editingEquipmentContribution && equipmentContributionHolderId <= 0) {
      notificationService.error(t('pleaseSelectAccountHolder'));
      return;
    }

    if (equipmentContributionAmount <= 0) {
      notificationService.error(t('contributionAmountMustBeGreaterThanZero'));
      return;
    }

    if (!editingEquipmentContribution && (selectedEquipmentForContribution.contributions || []).some((entry) => entry.accountHolderId === equipmentContributionHolderId)) {
      notificationService.error(t('accountHolderAlreadyAdded'));
      return;
    }

    try {
      setIsSavingEquipmentContribution(true);

      if (editingEquipmentContribution) {
        await equipmentHiringService.updateContribution(editingEquipmentContribution.id, {
          amountContributed: equipmentContributionAmount,
        });
      } else {
        await equipmentHiringService.addContribution({
          equipmentHiringId: selectedEquipmentForContribution.id,
          accountHolderId: equipmentContributionHolderId,
          amountContributed: equipmentContributionAmount,
        });
      }

      notificationService.success(editingEquipmentContribution ? t('equipmentContributorUpdatedSuccessfully') : t('equipmentContributorAddedSuccessfully'));
      handleCloseEquipmentContributionModal();
      await loadData(selectedDate, { forceRefresh: true });
    } catch (err) {
      notificationService.error(editingEquipmentContribution ? t('failedToUpdateEquipmentContributor') : t('failedToAddEquipmentContributor'));
      console.error(err);
    } finally {
      setIsSavingEquipmentContribution(false);
    }
  };

  const handleRemoveEquipmentContributor = async (contributionId: number, contributorName: string) => {
    setConfirmationConfig({
      title: t('removeEquipmentContributorTitle'),
      message: `${t('removeEquipmentContributorConfirmPrefix')} ${contributorName} ${t('removeEquipmentContributorConfirmSuffix')}`,
      variant: 'danger',
      onConfirm: async () => {
        try {
          await equipmentHiringService.removeContribution(contributionId);
          notificationService.success(t('equipmentContributorRemovedSuccessfully'));
          setShowConfirmation(false);
          await loadData(selectedDate, { forceRefresh: true });
        } catch (err) {
          notificationService.error(t('failedToRemoveEquipmentContributor'));
          console.error(err);
        }
      },
    });
    setShowConfirmation(true);
  };

  const handleShowEquipmentIncomeRecipientModal = (equipment: EquipmentHiring, recipient?: EquipmentHiringIncomeRecipient) => {
    setSelectedEquipmentForIncomeRecipient(equipment);

    if (recipient) {
      setEditingEquipmentIncomeRecipient(recipient);
      setEquipmentIncomeRecipientHolderId(recipient.accountHolderId);
      setEquipmentIncomeRecipientAmount(recipient.incomeShare);
    } else {
      setEditingEquipmentIncomeRecipient(null);
      setEquipmentIncomeRecipientHolderId(0);
      setEquipmentIncomeRecipientAmount(0);
    }

    setShowEquipmentIncomeRecipientModal(true);
  };

  const handleCloseEquipmentIncomeRecipientModal = () => {
    if (isSavingEquipmentIncomeRecipient) return;

    setShowEquipmentIncomeRecipientModal(false);
    setSelectedEquipmentForIncomeRecipient(null);
    setEditingEquipmentIncomeRecipient(null);
    setEquipmentIncomeRecipientHolderId(0);
    setEquipmentIncomeRecipientAmount(0);
  };

  const handleSaveEquipmentIncomeRecipient = async () => {
    if (!selectedEquipmentForIncomeRecipient) {
      return;
    }

    if (!editingEquipmentIncomeRecipient && equipmentIncomeRecipientHolderId <= 0) {
      notificationService.error(t('pleaseSelectAccountHolder'));
      return;
    }

    if (equipmentIncomeRecipientAmount <= 0) {
      notificationService.error(t('contributionAmountMustBeGreaterThanZero'));
      return;
    }

    if (!editingEquipmentIncomeRecipient && (selectedEquipmentForIncomeRecipient.incomeRecipients || []).some((entry) => entry.accountHolderId === equipmentIncomeRecipientHolderId)) {
      notificationService.error(t('accountHolderAlreadyAdded'));
      return;
    }

    try {
      setIsSavingEquipmentIncomeRecipient(true);

      if (editingEquipmentIncomeRecipient) {
        await equipmentHiringService.updateIncomeRecipient(editingEquipmentIncomeRecipient.id, {
          incomeShare: equipmentIncomeRecipientAmount,
        });
      } else {
        await equipmentHiringService.addIncomeRecipient({
          equipmentHiringId: selectedEquipmentForIncomeRecipient.id,
          accountHolderId: equipmentIncomeRecipientHolderId,
          incomeShare: equipmentIncomeRecipientAmount,
        });
      }

      notificationService.success(editingEquipmentIncomeRecipient ? t('incomeRecipientUpdatedSuccessfully') : t('incomeRecipientAddedSuccessfully'));
      handleCloseEquipmentIncomeRecipientModal();
      await loadData(selectedDate, { forceRefresh: true });
    } catch (err) {
      notificationService.error(editingEquipmentIncomeRecipient ? t('failedToUpdateIncomeRecipient') : t('failedToAddIncomeRecipient'));
      console.error(err);
    } finally {
      setIsSavingEquipmentIncomeRecipient(false);
    }
  };

  const handleRemoveEquipmentIncomeRecipient = async (recipientId: number, recipientName: string) => {
    setConfirmationConfig({
      title: t('removeIncomeRecipientTitle'),
      message: `${t('removeIncomeRecipientConfirmPrefix')} ${recipientName} ${t('removeIncomeRecipientConfirmSuffix')}`,
      variant: 'danger',
      onConfirm: async () => {
        try {
          await equipmentHiringService.removeIncomeRecipient(recipientId);
          notificationService.success(t('incomeRecipientRemovedSuccessfully'));
          setShowConfirmation(false);
          await loadData(selectedDate, { forceRefresh: true });
        } catch (err) {
          notificationService.error(t('failedToRemoveIncomeRecipient'));
          console.error(err);
        }
      },
    });
    setShowConfirmation(true);
  };

  const monthlyStats = useMemo(() => {
    const totalFarmWorkCost = farmWorks.reduce((sum, work) => sum + work.cost, 0);
    const totalContributions = farmWorks.reduce(
      (sum, work) => sum + work.contributions.reduce((contributionSum, contribution) => contributionSum + contribution.amountContributed, 0),
      0
    );
    const contributionBalance = totalContributions - totalFarmWorkCost;
    const coveragePercentage = totalFarmWorkCost > 0 ? (totalContributions / totalFarmWorkCost) * 100 : 0;
    const isCovered = totalFarmWorkCost > 0 && totalContributions >= totalFarmWorkCost;
    const contributorCount = new Set(
      farmWorks.flatMap((work) => work.contributions.map((contribution) => contribution.accountHolderId))
    ).size;

    return {
      totalFarmWorkCost,
      totalContributions,
      contributionBalance,
      coveragePercentage,
      isCovered,
      contributorCount,
    };
  }, [farmWorks]);

  const activeAccountHolders = useMemo(
    () => allAccountHolders.filter((holder) => isHolderTrackableForMonth(holder, selectedDate)),
    [allAccountHolders, selectedDate]
  );

  const workTotalPages = Math.max(1, Math.ceil(farmWorks.length / workPageSize));
  const safeWorkCurrentPage = Math.min(workCurrentPage, workTotalPages);
  const pagedFarmWorks = useMemo(() => {
    const startIndex = (safeWorkCurrentPage - 1) * workPageSize;
    return farmWorks.slice(startIndex, startIndex + workPageSize);
  }, [farmWorks, safeWorkCurrentPage, workPageSize]);
  const workPageStart = farmWorks.length === 0 ? 0 : (safeWorkCurrentPage - 1) * workPageSize + 1;
  const workPageEnd = Math.min(safeWorkCurrentPage * workPageSize, farmWorks.length);

  const typeTotalPages = Math.max(1, Math.ceil(farmWorkTypes.length / typePageSize));
  const safeTypeCurrentPage = Math.min(typeCurrentPage, typeTotalPages);
  const pagedFarmWorkTypes = useMemo(() => {
    const startIndex = (safeTypeCurrentPage - 1) * typePageSize;
    return farmWorkTypes.slice(startIndex, startIndex + typePageSize);
  }, [farmWorkTypes, safeTypeCurrentPage, typePageSize]);
  const typePageStart = farmWorkTypes.length === 0 ? 0 : (safeTypeCurrentPage - 1) * typePageSize + 1;
  const typePageEnd = Math.min(safeTypeCurrentPage * typePageSize, farmWorkTypes.length);

  const equipmentStats = useMemo(() => {
    const totalCost = equipmentHirings.reduce((sum, item) => sum + item.cost, 0);
    const totalIncome = equipmentHirings.reduce((sum, item) => sum + item.incomeAmount, 0);
    const totalHours = equipmentHirings.reduce((sum, item) => sum + item.hoursHired, 0);
    const totalContributions = equipmentHirings.reduce(
      (sum, item) => sum + (item.contributions || []).reduce((contribSum, contribution) => contribSum + contribution.amountContributed, 0),
      0
    );

    return {
      totalCost,
      totalIncome,
      totalHours,
      totalContributions,
    };
  }, [equipmentHirings]);

  const equipmentTotalPages = Math.max(1, Math.ceil(equipmentHirings.length / equipmentPageSize));
  const safeEquipmentCurrentPage = Math.min(equipmentCurrentPage, equipmentTotalPages);
  const pagedEquipmentHirings = useMemo(() => {
    const startIndex = (safeEquipmentCurrentPage - 1) * equipmentPageSize;
    return equipmentHirings.slice(startIndex, startIndex + equipmentPageSize);
  }, [equipmentHirings, safeEquipmentCurrentPage, equipmentPageSize]);
  const equipmentPageStart = equipmentHirings.length === 0 ? 0 : (safeEquipmentCurrentPage - 1) * equipmentPageSize + 1;
  const equipmentPageEnd = Math.min(safeEquipmentCurrentPage * equipmentPageSize, equipmentHirings.length);

  const activeSectionMeta = activeSection === 'farmWork'
    ? {
        title: t('farmWorkItems'),
        subtitle: t('activeSectionFarmWorkSubtitle'),
        addLabel: t('addFarmWorks'),
        addAria: t('addFarmWorks'),
      }
    : {
        title: t('equipmentHiring'),
        subtitle: t('activeSectionEquipmentSubtitle'),
        addLabel: t('addEquipmentHiring'),
        addAria: t('addEquipmentHiring'),
      };

  if (loading && !hasLoadedOnce) {
    return (
      <Container className="py-5 page-loading-shell">
        <div className="page-loading-header">
          <div className="page-loading-title page-skeleton-shimmer" aria-hidden="true" />
          <div className="page-loading-subtitle">
            <LoadingIndicator label={t('loading')} className="system-loading-status-card-inline" showLines={false} />
          </div>
        </div>
        <div className="row g-4">
          <div className="col-lg-3 col-md-6">
            <div className="page-loading-block page-skeleton-shimmer" aria-hidden="true" />
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="page-loading-block page-skeleton-shimmer" aria-hidden="true" />
          </div>
          <div className="col-lg-6">
            <div className="page-loading-block page-loading-block-tall page-skeleton-shimmer" aria-hidden="true" />
          </div>
          <div className="col-12">
            <div className="page-loading-block page-skeleton-shimmer" aria-hidden="true" />
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container className="py-5" aria-busy={loading || isMonthRefreshing || isSavingType || isSavingWork || isSavingEquipment || isSavingContribution || isUpdatingContribution}>
      <div className="farmwork-hero page-header-panel">
        <div className="farmwork-header d-flex justify-content-between align-items-center">
          <div>
            <h1 className="mb-1 page-header-title">
              <span className="page-header-title-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                  <path d="M7 3.5h10a2.5 2.5 0 0 1 2.5 2.5v12a2.5 2.5 0 0 1-2.5 2.5H7A2.5 2.5 0 0 1 4.5 18V6A2.5 2.5 0 0 1 7 3.5z" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
                  <path d="M8 8h8M8 11.5h8M8 15h5.5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
                </svg>
              </span>
              {t('farmWorkAndEquipmentTitle')}
            </h1>
          </div>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="farmwork-section-switcher mb-3" aria-label={t('expenseEquipmentSectionSwitcherAria')}>
        <div className="farmwork-section-switcher-copy">
          <div className="farmwork-section-switcher-label">{t('manageArea')}</div>
          <div className="farmwork-section-switcher-title">{t('manageAreaSubtitle')}</div>
        </div>
        <div className="farmwork-section-switcher-tabs" role="tablist" aria-label={t('expenseEquipmentViewsAria')}>
          <button
            type="button"
            className={`farmwork-section-tab ${activeSection === 'farmWork' ? 'is-active' : ''}`}
            onClick={() => setActiveSection('farmWork')}
            role="tab"
            aria-selected={activeSection === 'farmWork'}
          >
            <span className="farmwork-section-tab-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                <rect x="4" y="4.5" width="16" height="15" rx="2.8" fill="none" stroke="currentColor" strokeWidth="1.8" />
                <path d="M7.5 9h9M7.5 12.3h9M7.5 15.6h6.2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </span>
            <span className="farmwork-section-tab-text">
              <span className="farmwork-section-tab-title">{t('farmWorkItems')}</span>
              <span className="farmwork-section-tab-subtitle">{t('expensesTabSubtitle')}</span>
            </span>
          </button>
          <button
            type="button"
            className={`farmwork-section-tab ${activeSection === 'equipment' ? 'is-active' : ''}`}
            onClick={() => setActiveSection('equipment')}
            role="tab"
            aria-selected={activeSection === 'equipment'}
          >
            <span className="farmwork-section-tab-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                <path d="M13.8 4.5a3.2 3.2 0 0 1 5.7 2.3l-4.1 4.1-3.6-3.6 2-2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M3.8 19.4l5.2-1.3 6.4-6.4-3.9-3.9-6.4 6.4-1.3 5.2z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M15.6 5.8l2.6 2.6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </span>
            <span className="farmwork-section-tab-text">
              <span className="farmwork-section-tab-title">{t('equipmentHiring')}</span>
              <span className="farmwork-section-tab-subtitle">{t('equipmentHiringTabSubtitle')}</span>
            </span>
          </button>
        </div>
      </div>

      <div className="farmwork-actions-strip mb-4">
        <div className="farmwork-actions-context">
          <div className="farmwork-actions-context-label">{t('activeSection')}</div>
          <div className="farmwork-actions-context-title">{activeSectionMeta.title}</div>
          <div className="farmwork-actions-context-subtitle">{activeSectionMeta.subtitle}</div>
        </div>
        <div className="d-flex align-items-center gap-2 farmwork-header-actions">
          <Button
            variant="light"
            className="farmwork-action-btn farmwork-action-btn-icon"
            onClick={() => {
              if (activeSection === 'farmWork') {
                handleShowWorkModal();
              } else {
                handleShowEquipmentModal();
              }
            }}
            aria-label={activeSectionMeta.addAria}
            title={activeSectionMeta.addAria}
          >
            <span className="farmwork-action-btn-label">{activeSectionMeta.addLabel}</span>
            <svg viewBox="0 0 24 24" className="farmwork-action-btn-icon-glyph" focusable="false" aria-hidden="true">
              {activeSection === 'farmWork' ? (
                <>
                  <rect x="4" y="4" width="16" height="16" rx="2.6" fill="none" stroke="currentColor" strokeWidth="1.9" />
                  <path d="M8 9h8M8 12h8M8 15h5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
                </>
              ) : (
                <>
                  <path d="M13.8 4.5a3.2 3.2 0 0 1 5.7 2.3l-4.1 4.1-3.6-3.6 2-2a3.2 3.2 0 0 1 0-0.8z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M3.8 19.4l5.2-1.3 6.4-6.4-3.9-3.9-6.4 6.4-1.3 5.2z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M15.6 5.8l2.6 2.6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </>
              )}
            </svg>
          </Button>
        </div>
      </div>

      {activeSection === 'farmWork' && (
      <div className="mt-4 farmwork-tab-panel">
            <div className="mb-4 farmwork-section-header">
              <div>
                <div className="d-flex gap-3 align-items-end farmwork-filter-row">
                  <div className="flex-grow-1 farmwork-month-filter" style={{ maxWidth: '250px' }}>
                    <Form.Label className="mb-2">{t('selectMonthYear')}</Form.Label>
                    <div className="app-date-picker-shell">
                      <DatePicker
                        selected={selectedDate}
                        onChange={(date: Date | null) => date && setSelectedDate(date)}
                        onChangeRaw={(e) => e?.preventDefault()}
                        onKeyDown={(e) => e.preventDefault()}
                        dateFormat="MMMM yyyy"
                        showMonthYearPicker
                        showFullMonthYearPicker
                        locale={language === 'af' ? 'af' : 'en-GB'}
                        className="form-control farmwork-month-picker"
                        placeholderText={t('selectDatePlaceholder')}
                      />
                      <span className="app-date-picker-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                          <rect x="3.5" y="5.5" width="17" height="15" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
                          <path d="M7.5 3.8v3.4M16.5 3.8v3.4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                          <path d="M3.8 9.2h16.4" fill="none" stroke="currentColor" strokeWidth="1.8" />
                          <path d="M8.2 12.8h3.1M8.2 16h5.8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                      </span>
                    </div>
                    {isMonthRefreshing && (
                      <Form.Text className="text-muted d-block mt-2">
                        <LoadingIndicator label={t('loading')} compact showLines={false} subtle />
                      </Form.Text>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-md-3">
                <div className="farmwork-stat-card farmwork-stat-cost h-100">
                  <div className="farmwork-stat-label">{t('totalFarmWorkCost')}</div>
                  <div className="farmwork-stat-value">{formatCurrency(monthlyStats.totalFarmWorkCost)}</div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="farmwork-stat-card farmwork-stat-contributions h-100">
                  <div className="farmwork-stat-label">{t('totalContributions')}</div>
                  <div className="farmwork-stat-value">{formatCurrency(monthlyStats.totalContributions)}</div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="farmwork-stat-card farmwork-stat-coverage h-100">
                  <div className="farmwork-stat-label">{t('coverage')}</div>
                  <div className="farmwork-stat-value">{monthlyStats.coveragePercentage.toFixed(1)}%</div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="farmwork-stat-card farmwork-stat-contributors h-100">
                  <div className="farmwork-stat-label">{t('contributors')}</div>
                  <div className="farmwork-stat-value">{monthlyStats.contributorCount}</div>
                </div>
              </div>
            </div>

            <div className="mb-4 farmwork-coverage-status">
              {monthlyStats.totalFarmWorkCost === 0 ? (
                <span className="text-muted">{t('noFarmWorkCostsForMonthYet')}</span>
              ) : monthlyStats.isCovered ? (
                <span className="status-covered">
                  {t('farmWorkCostsCoveredSurplusLabel')} <strong>{formatCurrency(monthlyStats.contributionBalance)}</strong>
                </span>
              ) : (
                <span className="status-shortfall">
                  {t('farmWorkCostsShortfallLabel')} <strong>{formatCurrency(Math.abs(monthlyStats.contributionBalance))}</strong>
                </span>
              )}
            </div>

            {farmWorkTypes.length > 0 && (
              <div className="farmwork-grid-panel">
                <div className="d-flex align-items-center gap-2 mb-4 farmwork-section-header">
                  <h5 className="mb-0 farmwork-section-title">{t('farmWorkItems')}</h5>
                  <span className="farmwork-grid-panel-count">{farmWorks.length}</span>
                </div>

                {farmWorks.length === 0 ? (
                  <Alert variant="info" className="mb-0">{t('noFarmWorkItemsForMonth')}</Alert>
                ) : (
                  <>
                  <div className="table-responsive standard-grid-shell dashboard-table-wrapper">
                    <Table className="dashboard-table mb-0 farmwork-items-accordion-table">
                      <thead>
                        <tr>
                          <th>{t('type')}</th>
                          <th>{t('description')}</th>
                          <th>{t('contributors')}</th>
                          <th>{t('contributions')}</th>
                          <th>{t('outstandingCostLabel')}</th>
                          <th>{t('actions')}</th>
                          <th className="farmwork-edit-column-header" aria-label={t('edit')}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {isMonthRefreshing ? (
                          <tr>
                            <td colSpan={7} className="text-center py-4 text-muted">
                              <div className="dashboard-grid-loading-message">
                                <LoadingIndicator label={t('loading')} subtle />
                              </div>
                            </td>
                          </tr>
                        ) : pagedFarmWorks.map((work) => {
                          const totalContributions = work.contributions.reduce((sum, c) => sum + c.amountContributed, 0);
                          const outstandingCost = work.cost - totalContributions;
                          const isFullyCovered = outstandingCost <= 0;
                          const coveragePercentage = work.cost > 0
                            ? Math.min((totalContributions / work.cost) * 100, 100)
                            : 0;
                          const isContributorsExpanded = !!expandedContributorRows[work.id];
                          const contributionCurrentPage = contributionCurrentPages[work.id] ?? 1;
                          const contributionTotalPages = Math.max(1, Math.ceil(work.contributions.length / contributionPageSize));
                          const safeContributionCurrentPage = Math.min(contributionCurrentPage, contributionTotalPages);
                          const contributionStartIndex = (safeContributionCurrentPage - 1) * contributionPageSize;
                          const pagedContributions = work.contributions.slice(
                            contributionStartIndex,
                            contributionStartIndex + contributionPageSize
                          );
                          const contributionPageStart = work.contributions.length === 0 ? 0 : contributionStartIndex + 1;
                          const contributionPageEnd = Math.min(contributionStartIndex + contributionPageSize, work.contributions.length);

                          return (
                            <React.Fragment key={work.id}>
                              <tr
                                className={`farmwork-item-row ${isContributorsExpanded ? 'farmwork-row-expanded' : ''}`.trim()}
                                onClick={() => handleToggleContributorRow(work.id)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleToggleContributorRow(work.id);
                                  }
                                }}
                                role="button"
                                tabIndex={0}
                                aria-expanded={isContributorsExpanded}
                                aria-label={`${t('contributors')} ${work.contributions.length}`}
                              >
                                <td>
                                  <div className="farmwork-item-header">
                                    <span className={`farmwork-row-chevron ${isContributorsExpanded ? 'is-open' : ''}`} aria-hidden="true">▸</span>
                                    <div className="farmwork-item-header-main">
                                      <div className="farmwork-cell-primary farmwork-item-header-title">{work.farmWorkTypeName}</div>
                                      <div className="farmwork-item-header-meta">
                                        {work.contributions.length} {t('contributors')} • {coveragePercentage.toFixed(1)}%
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <div className="farmwork-cell-secondary">{work.description || '-'}</div>
                                </td>
                                <td>
                                  <span className="farmwork-inline-pill">{work.contributions.length}</span>
                                </td>
                                <td>
                                  <div className="farmwork-cost-contribution-cell">
                                    <div className="farmwork-cost-contribution-primary fw-semibold">
                                      {formatCurrency(totalContributions)} / {formatCurrency(work.cost)}
                                    </div>
                                    <div className="farmwork-cost-contribution-meta">
                                      <div
                                        className="farmwork-cost-contribution-progress"
                                        role="progressbar"
                                        aria-label={t('coverage')}
                                        aria-valuemin={0}
                                        aria-valuemax={100}
                                        aria-valuenow={Math.round(coveragePercentage)}
                                      >
                                        <span
                                          className="farmwork-cost-contribution-progress-fill"
                                          style={{ width: `${coveragePercentage}%` }}
                                        />
                                      </div>
                                      <span className="farmwork-cost-contribution-progress-text text-muted">
                                        {coveragePercentage.toFixed(1)}%
                                      </span>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <span className={`farmwork-inline-pill ${isFullyCovered ? 'farmwork-pill-covered' : 'farmwork-pill-open'}`}>
                                    {formatCurrency(Math.max(outstandingCost, 0))}
                                  </span>
                                </td>
                                <td className="standard-grid-action-cell">
                                  <div className="standard-grid-action-wrap">
                                    <Button
                                      variant="danger"
                                      size="sm"
                                      className="farmwork-delete-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteWork(work.id);
                                      }}
                                    >
                                      {t('delete')}
                                    </Button>
                                  </div>
                                </td>
                                <td className="farmwork-edit-column-cell">
                                  <Button
                                    variant="warning"
                                    size="sm"
                                    className="farmwork-edit-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleShowWorkModal(work);
                                    }}
                                    aria-label={t('edit')}
                                    title={t('edit')}
                                  >
                                    <svg viewBox="0 0 24 24" className="farmwork-edit-btn-icon" focusable="false" aria-hidden="true">
                                      <path d="M5.5 3.5h9.75" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" />
                                      <path d="M5.5 3.5a3 3 0 0 0-3 3V18a3 3 0 0 0 3 3h11a3 3 0 0 0 3-3V9.25" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" strokeLinejoin="round" />
                                      <path d="M14.2 5.3l4.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" />
                                      <path d="M8 16.5l1-3.9 7.95-7.95a1.45 1.45 0 0 1 2.05 0l.35.35a1.45 1.45 0 0 1 0 2.05L11.4 15l-3.4 1.5z" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  </Button>
                                </td>
                              </tr>

                              {isContributorsExpanded && (
                                <tr className="farmwork-contributors-row">
                                  <td colSpan={7}>
                                    <div className="farmwork-contributors-panel">
                                      <div className="d-flex justify-content-between align-items-center mb-3">
                                        <span className="fw-semibold text-muted small">{t('contributors')}</span>
                                        {!isFullyCovered && (
                                          <Button variant="primary" size="sm" className="farmwork-primary-action" onClick={() => handleShowContributionModal(work)}>
                                            {t('addContributor')}
                                          </Button>
                                        )}
                                      </div>

                                      {work.contributions.length === 0 ? (
                                        <Alert variant="info" className="mb-0">{t('noContributionsYet')}</Alert>
                                      ) : (
                                        <>
                                          <div className="table-responsive standard-grid-shell dashboard-table-wrapper mb-2">
                                            <Table className="dashboard-table mb-0">
                                              <thead>
                                                <tr>
                                                  <th>{t('accountHolder')}</th>
                                                  <th>{t('amount')}</th>
                                                  <th>{t('actions')}</th>
                                                  <th className="farmwork-edit-column-header" aria-label={t('edit')}></th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {pagedContributions.map((contrib) => (
                                                  <tr key={contrib.id}>
                                                    <td>{contrib.accountHolderName}</td>
                                                    <td>{formatCurrency(contrib.amountContributed)}</td>
                                                    <td className="standard-grid-action-cell">
                                                      <div className="standard-grid-action-wrap">
                                                        <Button
                                                          variant="danger"
                                                          size="sm"
                                                          className="farmwork-delete-btn"
                                                          onClick={() => handleRemoveContribution(contrib.id)}
                                                        >
                                                          {t('delete')}
                                                        </Button>
                                                      </div>
                                                    </td>
                                                    <td className="farmwork-edit-column-cell">
                                                      <Button
                                                        variant="warning"
                                                        size="sm"
                                                        className="farmwork-edit-btn"
                                                        onClick={() => handleShowEditContributionModal(work, contrib)}
                                                        aria-label={t('edit')}
                                                        title={t('edit')}
                                                      >
                                                        <svg viewBox="0 0 24 24" className="farmwork-edit-btn-icon" focusable="false" aria-hidden="true">
                                                          <path d="M5.5 3.5h9.75" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" />
                                                          <path d="M5.5 3.5a3 3 0 0 0-3 3V18a3 3 0 0 0 3 3h11a3 3 0 0 0 3-3V9.25" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" strokeLinejoin="round" />
                                                          <path d="M14.2 5.3l4.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" />
                                                          <path d="M8 16.5l1-3.9 7.95-7.95a1.45 1.45 0 0 1 2.05 0l.35.35a1.45 1.45 0 0 1 0 2.05L11.4 15l-3.4 1.5z" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                      </Button>
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </Table>
                                          </div>

                                          <div className="dashboard-table-footer d-flex justify-content-between align-items-center">
                                            <div className="dashboard-table-meta text-muted">{t('showing')} {contributionPageStart}-{contributionPageEnd} {t('of')} {work.contributions.length}</div>
                                            <div className="d-flex align-items-center gap-2">
                                              <Form.Label className="mb-0 dashboard-rows-label">{t('rows')}</Form.Label>
                                              <div className="app-dropdown-arrow-wrapper" style={{ width: '90px' }}>
                                                <Form.Select
                                                  className="dashboard-rows-select"
                                                  value={contributionPageSize}
                                                  onChange={(e) => {
                                                    setContributionPageSize(parseInt(e.target.value, 10));
                                                    setContributionCurrentPages((previous) => ({ ...previous, [work.id]: 1 }));
                                                  }}
                                                  disabled={isMonthRefreshing}
                                                >
                                                  <option value={10}>10</option>
                                                  <option value={25}>25</option>
                                                  <option value={50}>50</option>
                                                </Form.Select>
                                                <DropdownArrow />
                                              </div>
                                              <Pagination className="mb-0 dashboard-pagination">
                                                <Pagination.First
                                                  onClick={() => setContributionCurrentPages((previous) => ({ ...previous, [work.id]: 1 }))}
                                                  disabled={safeContributionCurrentPage === 1 || isMonthRefreshing}
                                                />
                                                <Pagination.Prev
                                                  onClick={() => setContributionCurrentPages((previous) => ({
                                                    ...previous,
                                                    [work.id]: Math.max(1, safeContributionCurrentPage - 1),
                                                  }))}
                                                  disabled={safeContributionCurrentPage === 1 || isMonthRefreshing}
                                                />
                                                <Pagination.Item active>{safeContributionCurrentPage}</Pagination.Item>
                                                {contributionTotalPages > 1 && <Pagination.Item disabled>/ {contributionTotalPages}</Pagination.Item>}
                                                <Pagination.Next
                                                  onClick={() => setContributionCurrentPages((previous) => ({
                                                    ...previous,
                                                    [work.id]: Math.min(contributionTotalPages, safeContributionCurrentPage + 1),
                                                  }))}
                                                  disabled={safeContributionCurrentPage === contributionTotalPages || isMonthRefreshing}
                                                />
                                                <Pagination.Last
                                                  onClick={() => setContributionCurrentPages((previous) => ({ ...previous, [work.id]: contributionTotalPages }))}
                                                  disabled={safeContributionCurrentPage === contributionTotalPages || isMonthRefreshing}
                                                />
                                              </Pagination>
                                            </div>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>

                  <div className="dashboard-table-footer d-flex justify-content-between align-items-center mt-3">
                    <div className="dashboard-table-meta text-muted">{t('showing')} {workPageStart}-{workPageEnd} {t('of')} {farmWorks.length}</div>
                    <div className="d-flex align-items-center gap-2">
                      <Form.Label className="mb-0 dashboard-rows-label">{t('rows')}</Form.Label>
                      <Form.Select
                        className="dashboard-rows-select"
                        value={workPageSize}
                        onChange={(e) => setWorkPageSize(parseInt(e.target.value, 10))}
                        disabled={isMonthRefreshing}
                        style={{ width: '90px' }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </Form.Select>
                      <Pagination className="mb-0 standard-grid-pagination dashboard-pagination">
                        <Pagination.First onClick={() => setWorkCurrentPage(1)} disabled={safeWorkCurrentPage === 1 || isMonthRefreshing} />
                        <Pagination.Prev onClick={() => setWorkCurrentPage((page) => Math.max(1, page - 1))} disabled={safeWorkCurrentPage === 1 || isMonthRefreshing} />
                        <Pagination.Item active>{safeWorkCurrentPage}</Pagination.Item>
                        {workTotalPages > 1 && <Pagination.Item disabled>/ {workTotalPages}</Pagination.Item>}
                        <Pagination.Next
                          onClick={() => setWorkCurrentPage((page) => Math.min(workTotalPages, page + 1))}
                          disabled={safeWorkCurrentPage === workTotalPages || isMonthRefreshing}
                        />
                        <Pagination.Last onClick={() => setWorkCurrentPage(workTotalPages)} disabled={safeWorkCurrentPage === workTotalPages || isMonthRefreshing} />
                      </Pagination>
                    </div>
                  </div>
                  </>
                )}
              </div>
            )}
      </div>
      )}

      {activeSection === 'equipment' && (
        <div className="mt-4 farmwork-tab-panel">
          <div className="mb-4 farmwork-section-header">
            <div className="d-flex gap-3 align-items-end farmwork-filter-row">
              <div className="flex-grow-1 farmwork-month-filter" style={{ maxWidth: '250px' }}>
                <Form.Label className="mb-2">{t('selectMonthYear')}</Form.Label>
                <div className="app-date-picker-shell">
                  <DatePicker
                    selected={selectedDate}
                    onChange={(date: Date | null) => date && setSelectedDate(date)}
                    onChangeRaw={(e) => e?.preventDefault()}
                    onKeyDown={(e) => e.preventDefault()}
                    dateFormat="MMMM yyyy"
                    showMonthYearPicker
                    showFullMonthYearPicker
                    locale={language === 'af' ? 'af' : 'en-GB'}
                    className="form-control farmwork-month-picker"
                    placeholderText={t('selectDatePlaceholder')}
                  />
                  <span className="app-date-picker-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                      <rect x="3.5" y="5.5" width="17" height="15" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M7.5 3.8v3.4M16.5 3.8v3.4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      <path d="M3.8 9.2h16.4" fill="none" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M8.2 12.8h3.1M8.2 16h5.8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-md-3">
              <div className="farmwork-stat-card farmwork-stat-cost h-100">
                <div className="farmwork-stat-label">{t('totalEquipmentCost')}</div>
                <div className="farmwork-stat-value">{formatCurrency(equipmentStats.totalCost)}</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="farmwork-stat-card farmwork-stat-contributions h-100">
                <div className="farmwork-stat-label">{t('totalContributorsLabel')}</div>
                <div className="farmwork-stat-value">{formatCurrency(equipmentStats.totalContributions)}</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="farmwork-stat-card farmwork-stat-coverage h-100">
                <div className="farmwork-stat-label">{t('incomeGenerated')}</div>
                <div className="farmwork-stat-value">{formatCurrency(equipmentStats.totalIncome)}</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="farmwork-stat-card farmwork-stat-contributors h-100">
                <div className="farmwork-stat-label">{t('hoursHired')}</div>
                <div className="farmwork-stat-value">{equipmentStats.totalHours.toFixed(2)}</div>
              </div>
            </div>
          </div>

          <div className="farmwork-grid-panel">
            <div className="d-flex align-items-center gap-2 mb-4 farmwork-section-header">
              <h5 className="mb-0 farmwork-section-title">{t('equipmentHiringItemsTitle')}</h5>
              <span className="farmwork-grid-panel-count">{equipmentHirings.length}</span>
            </div>

            {equipmentHirings.length === 0 ? (
              <Alert variant="info" className="mb-0">{t('noEquipmentHiringEntriesForSelectedMonth')}</Alert>
            ) : (
              <>
                <div className="table-responsive standard-grid-shell dashboard-table-wrapper">
                  <Table className="dashboard-table mb-0 farmwork-items-accordion-table">
                    <thead>
                      <tr>
                        <th>{t('equipmentLabel')}</th>
                        <th>{t('hoursHired')}</th>
                        <th>{t('cost')}</th>
                        <th>{t('income')}</th>
                        <th>{t('contributors')}</th>
                        <th>{t('incomeRecipients')}</th>
                        <th>{t('actions')}</th>
                        <th className="farmwork-edit-column-header" aria-label={t('edit')}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedEquipmentHirings.map((item) => {
                        const totalEquipmentContributions = (item.contributions || []).reduce((sum, contribution) => sum + contribution.amountContributed, 0);
                        const contributionCoverage = item.cost > 0 ? Math.min((totalEquipmentContributions / item.cost) * 100, 100) : 0;
                        const isExpanded = !!expandedEquipmentRows[item.id];
                        const parsedHiringDate = parseEquipmentHiringDate(item.hiringDate, new Date(item.year, item.month - 1, 1));

                        return (
                          <React.Fragment key={item.id}>
                            <tr
                              className={`farmwork-item-row ${isExpanded ? 'farmwork-row-expanded' : ''}`.trim()}
                              onClick={() => handleToggleEquipmentRow(item.id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleToggleEquipmentRow(item.id);
                                }
                              }}
                              role="button"
                              tabIndex={0}
                              aria-expanded={isExpanded}
                              aria-label={`${t('equipmentRowAriaPrefix')} ${item.equipmentDescription}`}
                            >
                              <td>
                                <div className="farmwork-item-header">
                                  <span className={`farmwork-row-chevron ${isExpanded ? 'is-open' : ''}`} aria-hidden="true">▸</span>
                                  <div className="farmwork-item-header-main">
                                    <div className="farmwork-cell-primary farmwork-item-header-title">{item.equipmentDescription}</div>
                                    <div className="farmwork-item-header-meta">
                                      {(item.contributions?.length || 0)} {t('contributors')} • {(item.incomeRecipients?.length || 0)} {t('incomeRecipients')} • {formatDate(parsedHiringDate)}
                                    </div>
                                  </div>
                                </div>
                                <div className="farmwork-cell-secondary ms-4 ps-1">{item.description || '-'}</div>
                              </td>
                              <td>{item.hoursHired.toFixed(2)}</td>
                              <td>{formatCurrency(item.cost)}</td>
                              <td>{formatCurrency(item.incomeAmount)}</td>
                              <td>
                                <div className="farmwork-cost-contribution-cell">
                                  <div className="farmwork-cost-contribution-primary fw-semibold">
                                    {formatCurrency(totalEquipmentContributions)} / {formatCurrency(item.cost)}
                                  </div>
                                  <div className="farmwork-cost-contribution-meta">
                                    <div className="farmwork-cost-contribution-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(contributionCoverage)}>
                                      <span className="farmwork-cost-contribution-progress-fill" style={{ width: `${contributionCoverage}%` }} />
                                    </div>
                                    <span className="farmwork-cost-contribution-progress-text text-muted">{contributionCoverage.toFixed(1)}%</span>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span className="farmwork-inline-pill">{item.incomeRecipients?.length || 0}</span>
                              </td>
                              <td className="standard-grid-action-cell">
                                <div className="standard-grid-action-wrap">
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    className="farmwork-delete-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteEquipmentHiring(item.id);
                                    }}
                                  >
                                    {t('delete')}
                                  </Button>
                                </div>
                              </td>
                              <td className="farmwork-edit-column-cell">
                                <Button
                                  variant="warning"
                                  size="sm"
                                  className="farmwork-edit-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleShowEquipmentModal(item);
                                  }}
                                  aria-label={t('edit')}
                                  title={t('edit')}
                                >
                                  <svg viewBox="0 0 24 24" className="farmwork-edit-btn-icon" focusable="false" aria-hidden="true">
                                    <path d="M5.5 3.5h9.75" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" />
                                    <path d="M5.5 3.5a3 3 0 0 0-3 3V18a3 3 0 0 0 3 3h11a3 3 0 0 0 3-3V9.25" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M14.2 5.3l4.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" />
                                    <path d="M8 16.5l1-3.9 7.95-7.95a1.45 1.45 0 0 1 2.05 0l.35.35a1.45 1.45 0 0 1 0 2.05L11.4 15l-3.4 1.5z" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                </Button>
                              </td>
                            </tr>

                            {isExpanded && (
                              <tr className="farmwork-contributors-row">
                                <td colSpan={8}>
                                  <div className="farmwork-contributors-panel">
                                    <div className="row g-3">
                                      <div className="col-lg-6">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                          <span className="fw-semibold text-muted small">{t('contributors')}</span>
                                          <Button
                                            variant="primary"
                                            size="sm"
                                            className="farmwork-primary-action"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleShowEquipmentContributionModal(item);
                                            }}
                                          >
                                            {t('addContributor')}
                                          </Button>
                                        </div>

                                        {(item.contributions || []).length === 0 ? (
                                          <Alert variant="info" className="mb-0">{t('noContributorsYet')}</Alert>
                                        ) : (
                                          <div className="table-responsive standard-grid-shell dashboard-table-wrapper mb-0">
                                            <Table className="dashboard-table mb-0">
                                              <thead>
                                                <tr>
                                                  <th>{t('accountHolder')}</th>
                                                  <th>{t('amount')}</th>
                                                  <th>{t('actions')}</th>
                                                  <th className="farmwork-edit-column-header" aria-label={t('edit')}></th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {(item.contributions || []).map((contribution) => (
                                                  <tr key={contribution.id}>
                                                    <td>{contribution.accountHolderName}</td>
                                                    <td>{formatCurrency(contribution.amountContributed)}</td>
                                                    <td className="standard-grid-action-cell">
                                                      <div className="standard-grid-action-wrap">
                                                        <Button
                                                          variant="danger"
                                                          size="sm"
                                                          className="farmwork-delete-btn"
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRemoveEquipmentContributor(contribution.id, contribution.accountHolderName);
                                                          }}
                                                        >
                                                          {t('delete')}
                                                        </Button>
                                                      </div>
                                                    </td>
                                                    <td className="farmwork-edit-column-cell">
                                                      <Button
                                                        variant="warning"
                                                        size="sm"
                                                        className="farmwork-edit-btn"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleShowEquipmentContributionModal(item, contribution);
                                                        }}
                                                        aria-label={t('edit')}
                                                        title={t('edit')}
                                                      >
                                                        <svg viewBox="0 0 24 24" className="farmwork-edit-btn-icon" focusable="false" aria-hidden="true">
                                                          <path d="M5.5 3.5h9.75" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" />
                                                          <path d="M5.5 3.5a3 3 0 0 0-3 3V18a3 3 0 0 0 3 3h11a3 3 0 0 0 3-3V9.25" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" strokeLinejoin="round" />
                                                          <path d="M14.2 5.3l4.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" />
                                                          <path d="M8 16.5l1-3.9 7.95-7.95a1.45 1.45 0 0 1 2.05 0l.35.35a1.45 1.45 0 0 1 0 2.05L11.4 15l-3.4 1.5z" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                      </Button>
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </Table>
                                          </div>
                                        )}
                                      </div>

                                      <div className="col-lg-6">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                          <span className="fw-semibold text-muted small">{t('incomeRecipientsCredits')}</span>
                                          <Button
                                            variant="primary"
                                            size="sm"
                                            className="farmwork-primary-action"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleShowEquipmentIncomeRecipientModal(item);
                                            }}
                                          >
                                            {t('add')}
                                          </Button>
                                        </div>

                                        {(item.incomeRecipients || []).length === 0 ? (
                                          <Alert variant="info" className="mb-0">{t('noIncomeRecipientsYet')}</Alert>
                                        ) : (
                                          <div className="table-responsive standard-grid-shell dashboard-table-wrapper mb-0">
                                            <Table className="dashboard-table mb-0">
                                              <thead>
                                                <tr>
                                                  <th>{t('accountHolder')}</th>
                                                  <th>{t('incomeShare')}</th>
                                                  <th>{t('actions')}</th>
                                                  <th className="farmwork-edit-column-header" aria-label={t('edit')}></th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {(item.incomeRecipients || []).map((recipient) => (
                                                  <tr key={recipient.id}>
                                                    <td>{recipient.accountHolderName}</td>
                                                    <td>{formatCurrency(recipient.incomeShare)}</td>
                                                    <td className="standard-grid-action-cell">
                                                      <div className="standard-grid-action-wrap">
                                                        <Button
                                                          variant="danger"
                                                          size="sm"
                                                          className="farmwork-delete-btn"
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRemoveEquipmentIncomeRecipient(recipient.id, recipient.accountHolderName);
                                                          }}
                                                        >
                                                          {t('delete')}
                                                        </Button>
                                                      </div>
                                                    </td>
                                                    <td className="farmwork-edit-column-cell">
                                                      <Button
                                                        variant="warning"
                                                        size="sm"
                                                        className="farmwork-edit-btn"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleShowEquipmentIncomeRecipientModal(item, recipient);
                                                        }}
                                                        aria-label={t('edit')}
                                                        title={t('edit')}
                                                      >
                                                        <svg viewBox="0 0 24 24" className="farmwork-edit-btn-icon" focusable="false" aria-hidden="true">
                                                          <path d="M5.5 3.5h9.75" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" />
                                                          <path d="M5.5 3.5a3 3 0 0 0-3 3V18a3 3 0 0 0 3 3h11a3 3 0 0 0 3-3V9.25" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" strokeLinejoin="round" />
                                                          <path d="M14.2 5.3l4.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" />
                                                          <path d="M8 16.5l1-3.9 7.95-7.95a1.45 1.45 0 0 1 2.05 0l.35.35a1.45 1.45 0 0 1 0 2.05L11.4 15l-3.4 1.5z" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                      </Button>
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </Table>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>

                <div className="dashboard-table-footer d-flex justify-content-between align-items-center mt-3">
                  <div className="dashboard-table-meta text-muted">{t('showing')} {equipmentPageStart}-{equipmentPageEnd} {t('of')} {equipmentHirings.length}</div>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Label className="mb-0 dashboard-rows-label">{t('rows')}</Form.Label>
                    <Form.Select
                      className="dashboard-rows-select"
                      value={equipmentPageSize}
                      onChange={(e) => setEquipmentPageSize(parseInt(e.target.value, 10))}
                      style={{ width: '90px' }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </Form.Select>
                    <Pagination className="mb-0 standard-grid-pagination dashboard-pagination">
                      <Pagination.First onClick={() => setEquipmentCurrentPage(1)} disabled={safeEquipmentCurrentPage === 1} />
                      <Pagination.Prev onClick={() => setEquipmentCurrentPage((page) => Math.max(1, page - 1))} disabled={safeEquipmentCurrentPage === 1} />
                      <Pagination.Item active>{safeEquipmentCurrentPage}</Pagination.Item>
                      {equipmentTotalPages > 1 && <Pagination.Item disabled>/ {equipmentTotalPages}</Pagination.Item>}
                      <Pagination.Next
                        onClick={() => setEquipmentCurrentPage((page) => Math.min(equipmentTotalPages, page + 1))}
                        disabled={safeEquipmentCurrentPage === equipmentTotalPages}
                      />
                      <Pagination.Last onClick={() => setEquipmentCurrentPage(equipmentTotalPages)} disabled={safeEquipmentCurrentPage === equipmentTotalPages} />
                    </Pagination>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <Modal
        show={showTypesManagementModal}
        onHide={() => setShowTypesManagementModal(false)}
        size="xl"
        centered
        dialogClassName="farmwork-types-management-dialog"
        className="farmwork-modal farmwork-types-management-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('farmWorkTypes')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="farmwork-types-management-body">
            {farmWorkTypes.length === 0 ? (
              <div className="d-flex flex-column gap-3">
                <Alert variant="info" className="mb-0">{t('noFarmWorkTypesFound')}</Alert>
                <div>
                  <Button
                    variant="primary"
                    className="farmwork-primary-action"
                    onClick={() => {
                      handleShowTypeModal();
                    }}
                  >
                    {t('addFarmWorkType')}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <span className="farmwork-section-title">{t('farmWorkTypes')}</span>
                    <span className="farmwork-grid-panel-count">{farmWorkTypes.length}</span>
                  </div>
                  <Button
                    variant="primary"
                    className="farmwork-primary-action"
                    onClick={() => {
                      handleShowTypeModal();
                    }}
                  >
                    {t('addFarmWorkType')}
                  </Button>
                </div>
  
                <div className="table-responsive standard-grid-shell dashboard-table-wrapper farmwork-types-table-wrapper">
                  <Table className="dashboard-table mb-0">
                    <thead>
                      <tr>
                        <th>{t('name')}</th>
                        <th>{t('description')}</th>
                        <th>{t('createdDateLabel')}</th>
                        <th>{t('actions')}</th>
                        <th className="account-holders-edit-column-header" aria-label={t('edit')}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedFarmWorkTypes.map((type) => (
                        <tr key={type.id}>
                          <td>
                            <div className="farmwork-cell-primary">{type.name}</div>
                            {typeIdsWithContributors.has(type.id) && (
                              <span className="farmwork-inline-pill farmwork-type-linked-pill ms-2">{t('linkedToContributors')}</span>
                            )}
                          </td>
                          <td>
                            <div className="farmwork-cell-secondary">{type.description || '-'}</div>
                          </td>
                          <td>{formatDate(type.createdDate)}</td>
                          <td className="standard-grid-action-cell">
                            <div className="standard-grid-action-wrap">
                              <Button
                                variant="danger"
                                size="sm"
                                className="farmwork-delete-btn"
                                onClick={() => handleDeleteType(type.id)}
                                disabled={typeIdsWithContributors.has(type.id)}
                                title={
                                  typeIdsWithContributors.has(type.id)
                                    ? t('expenseTypeDeleteBlockedTooltip')
                                    : undefined
                                }
                              >
                                {t('delete')}
                              </Button>
                            </div>
                          </td>
                          <td className="account-holders-edit-column-cell">
                            <div className="standard-grid-action-wrap">
                              <Button
                                variant="warning"
                                size="sm"
                                className="account-holders-edit-btn"
                                onClick={() => {
                                  handleShowTypeModal(type);
                                }}
                                aria-label={t('edit')}
                                title={t('edit')}
                              >
                                <svg viewBox="0 0 24 24" className="account-holders-edit-btn-icon" focusable="false" aria-hidden="true">
                                  <path d="M5.5 3.5h9.75" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" />
                                  <path d="M5.5 3.5a3 3 0 0 0-3 3V18a3 3 0 0 0 3 3h11a3 3 0 0 0 3-3V9.25" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M14.2 5.3l4.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" />
                                  <path d="M8 16.5l1-3.9 7.95-7.95a1.45 1.45 0 0 1 2.05 0l.35.35a1.45 1.45 0 0 1 0 2.05L11.4 15l-3.4 1.5z" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <div className="dashboard-table-footer d-flex justify-content-between align-items-center mt-3">
                  <div className="dashboard-table-meta text-muted">{t('showing')} {typePageStart}-{typePageEnd} {t('of')} {farmWorkTypes.length}</div>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Label className="mb-0 dashboard-rows-label">{t('rows')}</Form.Label>
                    <Form.Select
                      className="dashboard-rows-select"
                      value={typePageSize}
                      onChange={(e) => setTypePageSize(parseInt(e.target.value, 10))}
                      style={{ width: '90px' }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </Form.Select>
                    <Pagination className="mb-0 standard-grid-pagination dashboard-pagination">
                      <Pagination.First onClick={() => setTypeCurrentPage(1)} disabled={safeTypeCurrentPage === 1} />
                      <Pagination.Prev onClick={() => setTypeCurrentPage((page) => Math.max(1, page - 1))} disabled={safeTypeCurrentPage === 1} />
                      <Pagination.Item active>{safeTypeCurrentPage}</Pagination.Item>
                      {typeTotalPages > 1 && <Pagination.Item disabled>/ {typeTotalPages}</Pagination.Item>}
                      <Pagination.Next
                        onClick={() => setTypeCurrentPage((page) => Math.min(typeTotalPages, page + 1))}
                        disabled={safeTypeCurrentPage === typeTotalPages}
                      />
                      <Pagination.Last onClick={() => setTypeCurrentPage(typeTotalPages)} disabled={safeTypeCurrentPage === typeTotalPages} />
                    </Pagination>
                  </div>
                </div>
              </>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowTypesManagementModal(false)}
          >
            {t('cancel')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Farm Work Type Modal */}
      <Modal show={showTypeModal} onHide={handleCancelTypeModal} className="farmwork-modal" centered>
          <Modal.Header closeButton={!isSavingType}>
          <Modal.Title>
            {editingTypeId ? `${t('edit')} ${t('farmWorkTypes')}` : t('addFarmWorkType')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body aria-busy={isSavingType}>
          <Form>
            {editingTypeId ? (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>{t('name')}</Form.Label>
                  <Form.Control
                    type="text"
                    value={typeFormData.name}
                    onChange={(e) => setTypeFormData({ ...typeFormData, name: e.target.value })}
                    onBlur={() => setTypeFormValidated(true)}
                    isInvalid={typeFormValidated && typeFormData.name.trim().length === 0}
                    autoFocus
                  />
                  <Form.Control.Feedback type="invalid">
                    {t('expenseTypeNameRequired')}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-0">
                  <Form.Label>{t('description')}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={typeFormData.description}
                    onChange={(e) => setTypeFormData({ ...typeFormData, description: e.target.value })}
                  />
                </Form.Group>
              </>
            ) : (
              <div className="farmwork-type-rows-form">
                <div className="farmwork-type-rows-header">
                  <span className="farmwork-type-rows-title">{t('addFarmWorkType')}</span>
                  <span className="farmwork-type-rows-count">{filledTypeRowsCount}</span>
                </div>
                <div className="farmwork-type-inline-row">
                  <Form.Group className="farmwork-type-inline-field farmwork-type-name-input mb-0">
                    <Form.Label className="farmwork-type-inline-label mb-0">{t('name')} *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={`${t('name')} *`}
                      value={draftTypeName}
                      onChange={(e) => setDraftTypeName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddDraftType();
                        }
                      }}
                      autoFocus
                    />
                  </Form.Group>
                  <Form.Group className="farmwork-type-inline-field farmwork-type-desc-input mb-0">
                    <Form.Label className="farmwork-type-inline-label mb-0">{t('description')}</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={`${t('description')} (${t('optional')})`}
                      value={draftTypeDescription}
                      onChange={(e) => setDraftTypeDescription(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddDraftType();
                        }
                      }}
                    />
                  </Form.Group>
                  <Button variant="outline-primary" size="sm" onClick={handleAddDraftType} className="farmwork-type-inline-add-btn">
                    {t('add')}
                  </Button>
                </div>

                {typeFormRows.length > 0 && (
                  <div className="farmwork-contributor-chip-list mt-2">
                    {typeFormRows.map((row, idx) => (
                      <button
                        type="button"
                        key={`${row.name}-${idx}`}
                        className="farmwork-contributor-chip farmwork-type-staged-chip"
                        onClick={() => handleRemoveDraftType(idx)}
                        title={`Click to remove ${row.name}`}
                        aria-label={`Click to remove ${row.name}`}
                      >
                        <span className="contributor-chip-name">
                          {row.name}{row.description ? ` - ${row.description}` : ''}
                        </span>
                        <span className="contributor-chip-remove">×</span>
                      </button>
                    ))}
                    <Button type="button" variant="outline-danger" size="sm" onClick={handleClearDraftTypes} className="btn-clear-contributors">
                      {t('clearAll')}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelTypeModal} disabled={isSavingType}>
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveFarmWorkType}
            disabled={
              editingTypeId
                ? typeFormData.name.trim().length === 0
                : filledTypeRowsCount === 0
            }
              className="d-inline-flex align-items-center gap-2"
            >
              {isSavingType && <LoadingIndicator label={t('savingAction')} inline compact showLines={false} />}
              {isSavingType
                ? t('savingAction')
                : editingTypeId
                  ? t('update')
                  : filledTypeRowsCount > 1
                    ? `${t('add')} (${filledTypeRowsCount})`
                    : t('add')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Farm Work Modal */}
      <Modal show={showWorkModal} onHide={handleCancelWorkModal} className="farmwork-modal farmwork-work-modal" centered>
          <Modal.Header closeButton={!isSavingWork}>
          <Modal.Title>{editingWorkId ? `${t('edit')} ${t('farmWork')}` : t('addFarmWorks')}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="farmwork-work-modal-body" aria-busy={isSavingWork}>
          <Form>

            {!editingWorkId && (
              <div className="farmwork-expense-modal-helper mb-2">
                <div className="farmwork-expense-modal-helper-title">{t('addExpenseModalHelperTitle')}</div>
                <div className="farmwork-expense-modal-helper-text">{t('addExpenseModalHelperText')}</div>
              </div>
            )}

            {/* ── CREATE MODE: Two-column layout ── */}
            {!editingWorkId && (
              <Row className="g-2 mb-2">

                {/* Left: Expense Types */}
                <Col md={6} className="d-flex flex-column">
                  <Form.Label className="farmwork-modal-contributors-title mb-2">
                    {t('farmWorkTypes')}
                    {selectedWorkTypeIds.length > 0 && (
                      <span className="farmwork-contributors-count-badge ms-1">{selectedWorkTypeIds.length}</span>
                    )}
                  </Form.Label>
                  <div className="farmwork-inline-type-create mb-2">
                    <Form.Control
                      type="text"
                      placeholder={`${t('name')} *`}
                      value={workModalNewTypeName}
                      onChange={(e) => setWorkModalNewTypeName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleCreateTypeFromWorkModal();
                        }
                      }}
                      disabled={isCreatingWorkModalType}
                    />
                    <Form.Control
                      type="text"
                      placeholder={`${t('description')} (${t('optional')})`}
                      value={workModalNewTypeDescription}
                      onChange={(e) => setWorkModalNewTypeDescription(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleCreateTypeFromWorkModal();
                        }
                      }}
                      disabled={isCreatingWorkModalType}
                    />
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={handleCreateTypeFromWorkModal}
                      disabled={isCreatingWorkModalType || workModalNewTypeName.trim().length === 0}
                    >
                      {isCreatingWorkModalType ? t('savingAction') : t('addFarmWorkType')}
                    </Button>
                  </div>
                  <div className="farmwork-type-selector-grid flex-grow-1">
                    {availableFarmWorkTypesForModal.length === 0 ? (
                      <Form.Text className="text-muted">{t('allFarmWorkTypesExistForMonth')}</Form.Text>
                    ) : (
                      availableFarmWorkTypesForModal.map((type) => (
                        <label
                          key={type.id}
                          className={`farmwork-type-check-card${selectedWorkTypeIds.includes(type.id) ? ' selected' : ''}`}
                        >
                          <input
                            type="checkbox"
                            id={`type-${type.id}`}
                            checked={selectedWorkTypeIds.includes(type.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedWorkTypeIds([...selectedWorkTypeIds, type.id]);
                              } else {
                                setSelectedWorkTypeIds(selectedWorkTypeIds.filter(id => id !== type.id));
                              }
                            }}
                            style={{ display: 'none' }}
                          />
                          <span className="farmwork-type-card-icon">{selectedWorkTypeIds.includes(type.id) ? '✓' : ''}</span>
                          <span className="farmwork-type-card-label">{type.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                </Col>

                {/* Right: Cost + Months */}
                <Col md={6} className="d-flex flex-column gap-2">

                  {/* Cost */}
                  <div>
                    <Form.Label className="mb-1">{t('cost')}</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min={0}
                      placeholder="0.00"
                      value={workFormData.cost === 0 ? '' : workFormData.cost}
                      onChange={(e) => {
                        setWorkFormData({ ...workFormData, cost: parseNonNegativeAmount(e.target.value) });
                      }}
                      onBlur={() => setWorkFormValidated(true)}
                      isInvalid={workFormValidated && workFormData.cost <= 0}
                      className="form-control-lg farmwork-cost-input"
                    />
                    <Form.Control.Feedback type="invalid">{t('expenseCostMustBeGreaterThanZero')}</Form.Control.Feedback>
                    {/* Inline cost status — visible only when contributors exist */}
                    {draftContributorsToAdd.length > 0 && (() => {
                      const totalContrib = draftContributorsToAdd.reduce((sum, c) => sum + c.amount, 0);
                      const diff = workFormData.cost - totalContrib;
                      return (
                        <div className="d-flex justify-content-between align-items-center mt-1 farmwork-cost-status-inline">
                          <span className={diff < 0 ? 'status-overpaid' : diff === 0 ? 'status-covered' : 'status-shortfall'}>
                            {diff < 0
                              ? `⚠ ${t('overpaid')}: ${formatCurrency(Math.abs(diff))}`
                              : diff === 0
                                ? `✓ ${t('fullyCovered')}`
                                : `${t('shortfallLabel')}: ${formatCurrency(diff)}`}
                          </span>
                          <span className="farmwork-cost-status-contrib">{t('totalContributionsCost')}: {formatCurrency(totalContrib)}</span>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Recurring Months */}
                  <div className="flex-grow-1">
                    <Form.Label className="d-flex justify-content-between align-items-center mb-1">
                      <span>{t('startMonthRecurringMonthsLabel')}</span>
                      {selectedRecurringMonthKeys.length > 0 && <span className="farmwork-month-count-badge">{selectedRecurringMonthKeys.length}</span>}
                    </Form.Label>
                    <div className="farmwork-recurring-compact">
                      <div className="d-flex gap-1 mb-1">
                        <div className="app-date-picker-shell app-date-picker-shell-compact flex-grow-1">
                          <DatePicker
                            selected={workModalDate}
                            onChange={(date: Date | null) => {
                              if (date) {
                                const month = date.getMonth() + 1;
                                const year = date.getFullYear();
                                setWorkModalDate(date);
                                setWorkFormData({ ...workFormData, month, year });
                              }
                            }}
                            onChangeRaw={(e) => e?.preventDefault()}
                            onKeyDown={(e) => e.preventDefault()}
                            dateFormat="MMMM yyyy"
                            showMonthYearPicker
                            showFullMonthYearPicker
                            locale={language === 'af' ? 'af' : 'en-GB'}
                            className="form-control form-control-sm farmwork-month-picker-compact"
                            placeholderText={t('selectDatePlaceholder')}
                          />
                          <span className="app-date-picker-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                              <rect x="3.5" y="5.5" width="17" height="15" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
                              <path d="M7.5 3.8v3.4M16.5 3.8v3.4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                              <path d="M3.8 9.2h16.4" fill="none" stroke="currentColor" strokeWidth="1.8" />
                              <path d="M8.2 12.8h3.1M8.2 16h5.8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                            </svg>
                          </span>
                        </div>
                        <Button variant="outline-primary" size="sm" onClick={handleAddRecurringMonth} style={{ whiteSpace: 'nowrap' }}>
                          {t('add')}
                        </Button>
                      </div>
                      <div className="farmwork-recurring-shortcuts-compact">
                        <Button variant="outline-secondary" size="sm" onClick={() => handleAddRecurringPreset(3)}>+3mo</Button>
                        <Button variant="outline-secondary" size="sm" onClick={() => handleAddRecurringPreset(6)}>+6mo</Button>
                        <Button variant="outline-secondary" size="sm" onClick={() => handleAddRecurringPreset(12)}>+12mo</Button>
                        <Button variant="outline-danger" size="sm" onClick={handleClearRecurringMonths} disabled={selectedRecurringMonthKeys.length === 0}>{t('clearAll')}</Button>
                      </div>
                      {selectedRecurringMonthKeys.length > 0 && (
                        <div className="farmwork-month-chip-list-compact mt-1">
                          {selectedRecurringMonthKeys.map((monthKey) => (
                            <button key={monthKey} type="button" className="farmwork-month-chip-compact" onClick={() => handleRemoveRecurringMonth(monthKey)}>
                              {monthKeyToLabel(monthKey)} <span>×</span>
                            </button>
                          ))}
                        </div>
                      )}
                      {recurringMonthWarning && (
                        <Alert variant="warning" className="mt-1 mb-0 py-1 px-2 small">{recurringMonthWarning}</Alert>
                      )}
                    </div>
                  </div>

                </Col>
              </Row>
            )}

            {/* ── EDIT MODE: compact two-col ── */}
            {editingWorkId && (
              <>
                <Row className="g-2 mb-2">
                  <Col xs={7}>
                    <Form.Label className="mb-1">{t('farmWorkTypes')}</Form.Label>
                    <Form.Select value={workFormData.farmWorkTypeId} disabled>
                      <option value="">{t('selectTypeOption')}</option>
                      {farmWorkTypes.map((type) => (
                        <option key={type.id} value={type.id}>{type.name}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={5}>
                    <Form.Label className="mb-1">{t('selectMonthYear')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formatMonthYear(new Date(workFormData.year, workFormData.month - 1, 1))}
                      readOnly
                    />
                  </Col>
                </Row>
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1">{t('cost')}</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min={0}
                    placeholder="0.00"
                    value={workFormData.cost === 0 ? '' : workFormData.cost}
                    onChange={(e) => {
                      setWorkFormData({ ...workFormData, cost: parseNonNegativeAmount(e.target.value) });
                    }}
                    onBlur={() => setWorkFormValidated(true)}
                    isInvalid={workFormValidated && workFormData.cost <= 0}
                  />
                  <Form.Control.Feedback type="invalid">{t('expenseCostMustBeGreaterThanZero')}</Form.Control.Feedback>
                </Form.Group>
              </>
            )}

            {/* Description — always visible, compact single row */}
            <Form.Group className="mb-2">
              <Form.Control
                as="textarea"
                rows={1}
                value={workFormData.description}
                onChange={(e) => setWorkFormData({ ...workFormData, description: e.target.value })}
                placeholder={`${t('description')} (${t('optional')})`}
                className="farmwork-description-input"
              />
            </Form.Group>

            {/* ── Contributors — compact horizontal row ── */}
            {!editingWorkId && (
              <div className="farmwork-modal-contributors-section">
                <div className="farmwork-modal-contributors-header">
                  <span className="farmwork-modal-contributors-title">
                    {t('contributors')}
                    <span className="text-muted ms-1" style={{ fontSize: '0.8rem', fontWeight: 400 }}>({t('optional')})</span>
                  </span>
                  {draftContributorsToAdd.length > 0 && (
                    <span className="farmwork-contributors-count-badge">{draftContributorsToAdd.length}</span>
                  )}
                </div>

                {/* Single-row input */}
                <div className="farmwork-contributor-inline-row">
                  <div className="app-dropdown-arrow-wrapper farmwork-contributor-holder-select">
                    <Form.Select
                      value={draftContributorHolderId}
                      onChange={(e) => setDraftContributorHolderId(parseInt(e.target.value))}
                    >
                      <option value="0">{t('selectAccountHolder')}</option>
                      {activeAccountHolders
                        .filter(h => !draftContributorsToAdd.some(c => c.accountHolderId === h.id))
                        .map((holder) => (
                          <option key={holder.id} value={holder.id}>{holder.name}</option>
                        ))}
                    </Form.Select>
                    <DropdownArrow />
                  </div>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min={0}
                    placeholder="0.00"
                    value={draftContributorAmount === 0 ? '' : draftContributorAmount}
                    onChange={(e) => {
                      setDraftContributorAmount(parseNonNegativeAmount(e.target.value));
                    }}
                    className="farmwork-contributor-amount-input"
                  />
                  <Button variant="outline-primary" size="sm" onClick={handleAddDraftContributor} style={{ whiteSpace: 'nowrap' }}>
                    {t('add')}
                  </Button>
                </div>

                {/* Contributor chips */}
                {draftContributorsToAdd.length > 0 && (
                  <div className="farmwork-contributor-chip-list mt-2">
                    {draftContributorsToAdd.map((contributor) => {
                      const holder = activeAccountHolders.find(h => h.id === contributor.accountHolderId);
                      return (
                        <button
                          type="button"
                          key={contributor.accountHolderId}
                          className="farmwork-contributor-chip"
                          onClick={() => handleRemoveDraftContributor(contributor.accountHolderId)}
                          title={`Click to remove ${holder?.name}`}
                        >
                          <span>{holder?.name} — {formatCurrency(contributor.amount)}</span>
                          <span className="contributor-chip-remove">×</span>
                        </button>
                      );
                    })}
                    <Button type="button" variant="outline-danger" size="sm" onClick={handleClearDraftContributors} className="btn-clear-contributors">
                      {t('clearAll')}
                    </Button>
                  </div>
                )}
              </div>
            )}

          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelWorkModal} disabled={isSavingWork}>
            {t('cancel')}
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSaveFarmWork}
              disabled={isSavingWork || workFormData.cost <= 0 || (!editingWorkId && (selectedWorkTypeIds.length === 0 || selectedRecurringMonthKeys.length === 0))}
              className="d-inline-flex align-items-center gap-2"
            >
              {isSavingWork && <LoadingIndicator label={t('savingAction')} inline compact showLines={false} />}
              {isSavingWork ? t('savingAction') : editingWorkId ? t('update') : t('add')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Equipment Hiring Modal */}
      <Modal show={showEquipmentModal} onHide={handleCancelEquipmentModal} className="farmwork-modal farmwork-work-modal" centered>
        <Modal.Header closeButton={!isSavingEquipment}>
          <Modal.Title>{editingEquipmentId ? t('editEquipmentHiring') : t('addEquipmentHiring')}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="farmwork-work-modal-body" aria-busy={isSavingEquipment}>
          <Form>
            <Row className="g-2 mb-2">
              <Col md={5}>
                <Form.Label className="mb-1">{t('equipmentLabel')}</Form.Label>
                <Form.Control
                  type="text"
                  value={equipmentFormData.equipmentDescription}
                  onChange={(e) => setEquipmentFormData({ ...equipmentFormData, equipmentDescription: e.target.value })}
                  placeholder={t('equipmentPlaceholder')}
                />
              </Col>
              <Col md={3}>
                <Form.Label className="mb-1">{t('date')}</Form.Label>
                <div className="app-date-picker-shell">
                  <DatePicker
                    selected={parseEquipmentHiringDate(equipmentFormData.hiringDate, selectedDate)}
                    onChange={(date: Date | null) => {
                      if (date) {
                        setEquipmentFormData({
                          ...equipmentFormData,
                          hiringDate: toIsoDateString(date),
                          month: date.getMonth() + 1,
                          year: date.getFullYear(),
                        });
                      }
                    }}
                    onChangeRaw={(e) => e?.preventDefault()}
                    dateFormat="dd/MM/yyyy"
                    className="form-control"
                    placeholderText={t('selectEquipmentDatePlaceholder')}
                    locale={language === 'af' ? 'af' : 'en-GB'}
                    portalId="root"
                  />
                  <span className="app-date-picker-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                      <rect x="3.5" y="5.5" width="17" height="15" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M7.5 3.8v3.4M16.5 3.8v3.4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      <path d="M3.8 9.2h16.4" fill="none" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M8.2 12.8h3.1M8.2 16h5.8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                  </span>
                </div>
              </Col>
              <Col md={2}>
                <Form.Label className="mb-1">{t('hoursHired')}</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min={0}
                  value={equipmentFormData.hoursHired === 0 ? '' : equipmentFormData.hoursHired}
                  onChange={(e) => setEquipmentFormData({ ...equipmentFormData, hoursHired: parseNonNegativeAmount(e.target.value) })}
                  placeholder="0.00"
                />
              </Col>
              <Col md={2}>
                <Form.Label className="mb-1">{t('cost')}</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min={0}
                  value={equipmentFormData.cost === 0 ? '' : equipmentFormData.cost}
                  onChange={(e) => setEquipmentFormData({ ...equipmentFormData, cost: parseNonNegativeAmount(e.target.value) })}
                  placeholder="0.00"
                />
              </Col>
            </Row>

            <Form.Group className="mb-2">
              <Form.Control
                as="textarea"
                rows={1}
                value={equipmentFormData.description}
                onChange={(e) => setEquipmentFormData({ ...equipmentFormData, description: e.target.value })}
                placeholder={`${t('description')} (${t('optional')})`}
                className="farmwork-description-input"
              />
            </Form.Group>

            {!editingEquipmentId && (
              <>
                <div className="farmwork-modal-contributors-section">
                  <div className="farmwork-modal-contributors-header">
                    <span className="farmwork-modal-contributors-title">
                      {t('contributors')}
                      <span className="text-muted ms-1" style={{ fontSize: '0.8rem', fontWeight: 400 }}>({t('optional')})</span>
                    </span>
                    {draftEquipmentContributors.length > 0 && (
                      <span className="farmwork-contributors-count-badge">{draftEquipmentContributors.length}</span>
                    )}
                  </div>

                  <div className="farmwork-contributor-inline-row">
                    <div className="app-dropdown-arrow-wrapper farmwork-contributor-holder-select">
                      <Form.Select
                        value={draftEquipmentContributorHolderId}
                        onChange={(e) => setDraftEquipmentContributorHolderId(parseInt(e.target.value, 10))}
                      >
                        <option value="0">{t('selectAccountHolder')}</option>
                        {activeAccountHolders
                          .filter((holder) => !draftEquipmentContributors.some((entry) => entry.accountHolderId === holder.id))
                          .map((holder) => (
                            <option key={holder.id} value={holder.id}>{holder.name}</option>
                          ))}
                      </Form.Select>
                      <DropdownArrow />
                    </div>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min={0}
                      placeholder="0.00"
                      value={draftEquipmentContributorAmount === 0 ? '' : draftEquipmentContributorAmount}
                      onChange={(e) => setDraftEquipmentContributorAmount(parseNonNegativeAmount(e.target.value))}
                      className="farmwork-contributor-amount-input"
                    />
                    <Button variant="outline-primary" size="sm" onClick={handleAddDraftEquipmentContributor} style={{ whiteSpace: 'nowrap' }}>
                      {t('add')}
                    </Button>
                  </div>

                  {draftEquipmentContributors.length > 0 && (
                    <div className="farmwork-contributor-chip-list mt-2">
                      {draftEquipmentContributors.map((entry) => {
                        const holder = activeAccountHolders.find((accountHolder) => accountHolder.id === entry.accountHolderId);
                        return (
                          <button
                            type="button"
                            key={entry.accountHolderId}
                            className="farmwork-contributor-chip"
                            onClick={() => handleRemoveDraftEquipmentContributor(entry.accountHolderId)}
                            title={`${t('clickToRemoveLabelPrefix')} ${holder?.name}`}
                          >
                            <span>{holder?.name} - {formatCurrency(entry.amount)}</span>
                            <span className="contributor-chip-remove">x</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="farmwork-modal-contributors-section">
                  <div className="farmwork-modal-contributors-header">
                    <span className="farmwork-modal-contributors-title">
                      {t('incomeRecipients')}
                      <span className="text-muted ms-1" style={{ fontSize: '0.8rem', fontWeight: 400 }}>({t('optional')})</span>
                    </span>
                    {draftEquipmentIncomeRecipients.length > 0 && (
                      <span className="farmwork-contributors-count-badge">{draftEquipmentIncomeRecipients.length}</span>
                    )}
                  </div>

                  <div className="farmwork-contributor-inline-row">
                    <div className="app-dropdown-arrow-wrapper farmwork-contributor-holder-select">
                      <Form.Select
                        value={draftIncomeRecipientHolderId}
                        onChange={(e) => setDraftIncomeRecipientHolderId(parseInt(e.target.value, 10))}
                      >
                        <option value="0">{t('selectAccountHolder')}</option>
                        {activeAccountHolders
                          .filter((holder) => !draftEquipmentIncomeRecipients.some((entry) => entry.accountHolderId === holder.id))
                          .map((holder) => (
                            <option key={holder.id} value={holder.id}>{holder.name}</option>
                          ))}
                      </Form.Select>
                      <DropdownArrow />
                    </div>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min={0}
                      placeholder="0.00"
                      value={draftIncomeRecipientAmount === 0 ? '' : draftIncomeRecipientAmount}
                      onChange={(e) => setDraftIncomeRecipientAmount(parseNonNegativeAmount(e.target.value))}
                      className="farmwork-contributor-amount-input"
                    />
                    <Button variant="outline-primary" size="sm" onClick={handleAddDraftIncomeRecipient} style={{ whiteSpace: 'nowrap' }}>
                      {t('add')}
                    </Button>
                  </div>

                  {draftEquipmentIncomeRecipients.length > 0 && (
                    <div className="farmwork-contributor-chip-list mt-2">
                      {draftEquipmentIncomeRecipients.map((entry) => {
                        const holder = activeAccountHolders.find((accountHolder) => accountHolder.id === entry.accountHolderId);
                        return (
                          <button
                            type="button"
                            key={entry.accountHolderId}
                            className="farmwork-contributor-chip"
                            onClick={() => handleRemoveDraftIncomeRecipient(entry.accountHolderId)}
                            title={`${t('clickToRemoveLabelPrefix')} ${holder?.name}`}
                          >
                            <span>{holder?.name} - {formatCurrency(entry.incomeShare)}</span>
                            <span className="contributor-chip-remove">x</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelEquipmentModal} disabled={isSavingEquipment}>
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveEquipmentHiring}
            disabled={isSavingEquipment || !equipmentFormData.equipmentDescription.trim() || !equipmentFormData.hiringDate || equipmentFormData.cost <= 0}
            className="d-inline-flex align-items-center gap-2"
          >
            {isSavingEquipment && <LoadingIndicator label={t('savingAction')} inline compact showLines={false} />}
            {isSavingEquipment ? t('savingAction') : editingEquipmentId ? t('update') : t('add')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Equipment Contributor Modal */}
      <Modal show={showEquipmentContributionModal} onHide={handleCloseEquipmentContributionModal} className="farmwork-modal" centered>
        <Modal.Header closeButton={!isSavingEquipmentContribution}>
          <Modal.Title>{editingEquipmentContribution ? `${t('edit')} ${t('contributor')}` : t('addContributor')}</Modal.Title>
        </Modal.Header>
        <Modal.Body aria-busy={isSavingEquipmentContribution}>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t('accountHolder')}</Form.Label>
              {editingEquipmentContribution ? (
                <Form.Control type="text" readOnly value={editingEquipmentContribution.accountHolderName} />
              ) : (
                <Form.Select
                  value={equipmentContributionHolderId}
                  onChange={(e) => setEquipmentContributionHolderId(parseInt(e.target.value, 10) || 0)}
                >
                  <option value="0">{t('selectAccountHolder')}</option>
                  {activeAccountHolders
                    .filter((holder) => !(selectedEquipmentForContribution?.contributions || []).some((entry) => entry.accountHolderId === holder.id))
                    .map((holder) => (
                      <option key={holder.id} value={holder.id}>{holder.name}</option>
                    ))}
                </Form.Select>
              )}
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label>{t('amount')}</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min={0}
                placeholder="0.00"
                value={equipmentContributionAmount === 0 ? '' : equipmentContributionAmount}
                onChange={(e) => setEquipmentContributionAmount(parseNonNegativeAmount(e.target.value))}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseEquipmentContributionModal} disabled={isSavingEquipmentContribution}>
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveEquipmentContribution}
            disabled={isSavingEquipmentContribution || equipmentContributionAmount <= 0 || (!editingEquipmentContribution && equipmentContributionHolderId <= 0)}
            className="d-inline-flex align-items-center gap-2"
          >
            {isSavingEquipmentContribution && <LoadingIndicator label={t('savingAction')} inline compact showLines={false} />}
            {isSavingEquipmentContribution ? t('savingAction') : editingEquipmentContribution ? t('update') : t('add')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Equipment Income Recipient Modal */}
      <Modal show={showEquipmentIncomeRecipientModal} onHide={handleCloseEquipmentIncomeRecipientModal} className="farmwork-modal" centered>
        <Modal.Header closeButton={!isSavingEquipmentIncomeRecipient}>
          <Modal.Title>{editingEquipmentIncomeRecipient ? `${t('edit')} ${t('incomeRecipient')}` : t('addIncomeRecipient')}</Modal.Title>
        </Modal.Header>
        <Modal.Body aria-busy={isSavingEquipmentIncomeRecipient}>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t('accountHolder')}</Form.Label>
              {editingEquipmentIncomeRecipient ? (
                <Form.Control type="text" readOnly value={editingEquipmentIncomeRecipient.accountHolderName} />
              ) : (
                <Form.Select
                  value={equipmentIncomeRecipientHolderId}
                  onChange={(e) => setEquipmentIncomeRecipientHolderId(parseInt(e.target.value, 10) || 0)}
                >
                  <option value="0">{t('selectAccountHolder')}</option>
                  {activeAccountHolders
                    .filter((holder) => !(selectedEquipmentForIncomeRecipient?.incomeRecipients || []).some((entry) => entry.accountHolderId === holder.id))
                    .map((holder) => (
                      <option key={holder.id} value={holder.id}>{holder.name}</option>
                    ))}
                </Form.Select>
              )}
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label>{t('incomeShare')}</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min={0}
                placeholder="0.00"
                value={equipmentIncomeRecipientAmount === 0 ? '' : equipmentIncomeRecipientAmount}
                onChange={(e) => setEquipmentIncomeRecipientAmount(parseNonNegativeAmount(e.target.value))}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseEquipmentIncomeRecipientModal} disabled={isSavingEquipmentIncomeRecipient}>
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveEquipmentIncomeRecipient}
            disabled={isSavingEquipmentIncomeRecipient || equipmentIncomeRecipientAmount <= 0 || (!editingEquipmentIncomeRecipient && equipmentIncomeRecipientHolderId <= 0)}
            className="d-inline-flex align-items-center gap-2"
          >
            {isSavingEquipmentIncomeRecipient && <LoadingIndicator label={t('savingAction')} inline compact showLines={false} />}
            {isSavingEquipmentIncomeRecipient ? t('savingAction') : editingEquipmentIncomeRecipient ? t('update') : t('add')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Contribution Modal */}
      <Modal show={showContributionModal} onHide={handleCloseContributionModal} className="farmwork-modal" centered>
        <Modal.Header closeButton={!isSavingContribution}>
          <Modal.Title>{t('addFarmWork')} {t('contributors')}</Modal.Title>
        </Modal.Header>
        <Modal.Body aria-busy={isSavingContribution}>
          <Form>
            {selectedFarmWork === null && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>{t('farmWorkLabel')}</Form.Label>
                  <Form.Select
                    value={selectedTypeIdForContribution || ''}
                    onChange={(e) => {
                      setSelectedTypeIdForContribution(parseInt(e.target.value) || 0);
                      setNewWorkCostForContribution(0);
                    }}
                    isInvalid={contributionFormValidated && selectedTypeIdForContribution <= 0}
                  >
                    <option value="">{t('farmWorkLabel')}</option>
                    {farmWorkTypes.length === 0 && (
                      <option value="" disabled>{t('noFarmWorkTypesFound')}</option>
                    )}
                    {farmWorkTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">{t('pleaseSelectFarmWorkType')}</Form.Control.Feedback>
                </Form.Group>
                {selectedTypeIdForContribution > 0 && (
                  <Form.Group className="mb-3">
                    <Form.Label>{t('totalCostLabel')}</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min={0}
                      placeholder="0.00"
                      value={newWorkCostForContribution === 0 ? '' : newWorkCostForContribution}
                      onChange={(e) => setNewWorkCostForContribution(parseNonNegativeAmount(e.target.value))}
                      onBlur={() => setContributionFormValidated(true)}
                      isInvalid={contributionFormValidated && newWorkCostForContribution <= 0}
                    />
                    <Form.Control.Feedback type="invalid">{t('expenseCostMustBeGreaterThanZero')}</Form.Control.Feedback>
                  </Form.Group>
                )}
              </>
            )}
            <Form.Group className="mb-3">
              <Form.Label>{t('accountHolder')}</Form.Label>
              <Form.Select
                value={selectedAccountHolderId}
                onChange={(e) => setSelectedAccountHolderId(parseInt(e.target.value))}
                onBlur={() => setContributionFormValidated(true)}
                isInvalid={contributionFormValidated && selectedAccountHolderId <= 0}
              >
                <option value="">{t('accountHolder')}</option>
                {activeAccountHolders.map((holder) => (
                  <option key={holder.id} value={holder.id}>
                    {holder.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">{t('selectAccountHolder')}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('amount')}</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min={0}
                placeholder="0.00"
                value={contributionAmount === 0 ? '' : contributionAmount}
                onChange={(e) => {
                  setContributionAmount(parseNonNegativeAmount(e.target.value));
                }}
                onBlur={() => setContributionFormValidated(true)}
                isInvalid={contributionFormValidated && contributionAmount <= 0}
              />
              <Form.Control.Feedback type="invalid">{t('contributionAmountMustBeGreaterThanZero')}</Form.Control.Feedback>
            </Form.Group>
            {selectedFarmWork && (() => {
              const existingTotal = selectedFarmWork.contributions.reduce((sum, c) => sum + c.amountContributed, 0);
              const remaining = Math.max(selectedFarmWork.cost - existingTotal - contributionAmount, 0);
              return (
                <div className="farmwork-cost-status-inline mt-2">
                  <span className="text-muted small">{t('farmWorkLabel')}: {selectedFarmWork.farmWorkTypeName} &nbsp;·&nbsp; {t('totalCostLabel')}: {formatCurrency(selectedFarmWork.cost)}</span>
                  {remaining > 0 && (
                    <span className="status-shortfall ms-2 small">{t('shortfallLabel')}: <strong>{formatCurrency(remaining)}</strong></span>
                  )}
                  {remaining === 0 && (
                    <span className="status-covered ms-2 small">✓ {t('fullyCovered')}</span>
                  )}
                </div>
              );
            })()}
            {selectedFarmWork === null && selectedTypeIdForContribution > 0 && newWorkCostForContribution > 0 && (() => {
              const remaining = Math.max(newWorkCostForContribution - contributionAmount, 0);
              const typeName = farmWorkTypes.find(t => t.id === selectedTypeIdForContribution)?.name ?? '';
              return (
                <div className="farmwork-cost-status-inline mt-2">
                  <span className="text-muted small">{t('farmWorkLabel')}: {typeName} &nbsp;·&nbsp; {t('totalCostLabel')}: {formatCurrency(newWorkCostForContribution)}</span>
                  {remaining > 0 && (
                    <span className="status-shortfall ms-2 small">{t('shortfallLabel')}: <strong>{formatCurrency(remaining)}</strong></span>
                  )}
                  {remaining === 0 && (
                    <span className="status-covered ms-2 small">✓ {t('fullyCovered')}</span>
                  )}
                </div>
              );
            })()}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseContributionModal} disabled={isSavingContribution}>
            {t('cancel')}
          </Button>
          <Button variant="primary" onClick={handleAddContribution} disabled={isSavingContribution || (selectedFarmWork === null && (selectedTypeIdForContribution <= 0 || newWorkCostForContribution <= 0)) || selectedAccountHolderId <= 0 || contributionAmount <= 0} className="d-inline-flex align-items-center gap-2">
            {isSavingContribution && <LoadingIndicator label={t('savingAction')} inline compact showLines={false} />}
            {isSavingContribution ? t('savingAction') : t('add')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Contribution Modal */}
      <Modal show={showEditContributionModal} onHide={handleCloseEditContributionModal} className="farmwork-modal" centered>
        <Modal.Header closeButton={!isUpdatingContribution}>
          <Modal.Title>{t('edit')} {t('amount')}</Modal.Title>
        </Modal.Header>
        <Modal.Body aria-busy={isUpdatingContribution}>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t('accountHolder')}</Form.Label>
              <Form.Control
                type="text"
                value={editingContribution?.accountHolderName || ''}
                readOnly
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('amount')}</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min={0.01}
                max={editingContributionMaxAmount || undefined}
                value={editingContributionAmount}
                onChange={(e) => {
                  const parsedValue = parseFloat(e.target.value);
                  if (Number.isNaN(parsedValue)) {
                    setEditingContributionAmount(0);
                    return;
                  }

                  const clampedValue = Math.min(
                    Math.max(parsedValue, 0.01),
                    editingContributionMaxAmount
                  );
                  setEditingContributionAmount(clampedValue);
                }}
              />
              <Form.Text className="text-muted">
                {t('maximumAllowedLabel')}: {formatCurrency(editingContributionMaxAmount)}
              </Form.Text>
            </Form.Group>
            {selectedFarmWork && (
              <div className="farmwork-cost-status-inline mt-2">
                <span className="text-muted small">{t('farmWorkLabel')}: {selectedFarmWork.farmWorkTypeName} &nbsp;·&nbsp; {t('totalCostLabel')}: {formatCurrency(selectedFarmWork.cost)}</span>
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseEditContributionModal} disabled={isUpdatingContribution}>
            {t('cancel')}
          </Button>
          <Button variant="primary" onClick={handleUpdateContribution} disabled={isUpdatingContribution} className="d-inline-flex align-items-center gap-2">
            {isUpdatingContribution && <LoadingIndicator label={t('savingAction')} inline compact showLines={false} />}
            {isUpdatingContribution ? t('savingAction') : t('update')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirmation Modal */}
      {confirmationConfig && (
        <ConfirmationModal
          show={showConfirmation}
          title={confirmationConfig.title}
          message={confirmationConfig.message}
          onConfirm={() => {
            confirmationConfig.onConfirm();
          }}
          onCancel={() => {
            setShowConfirmation(false);
            setConfirmationConfig(null);
          }}
          variant={confirmationConfig.variant || 'danger'}
          confirmText={confirmationConfig.confirmText || t('delete')}
        />
      )}
    </Container>
  );
};

export default FarmWorkPage;
